import { installNet } from './net/session.js';
// SLIME DUEL — boot, main loop and game-flow orchestration (menus ⇄ attract mode ⇄ matches ⇄ results).
import * as Daily from './game/daily.js';
import * as Elo from './game/elo.js';
import * as KU from './game/kit-unlocks.js';
import { savedCos } from './game/cosmetics.js';
import * as THREE from 'three';
import { G, on, emit, clamp, damp } from './core/ctx.js';
import { Renderer } from './core/renderer.js';
import { Input } from './core/input.js';
import { mapTheme,
  DEFAULT_SETTINGS, QUALITY, TEAM_PALETTES, COLORBLIND_PALETTE, TEAM_NAMES, WEAPONS, WEAPON_ORDER, WEAPON_SUCCESSOR, ZONES, SUB, SUBS, SUB_ORDER, SPECIALS, SPECIAL_ORDER,
  MAPS, DIFFICULTY, PLAYER, PROGRESSION, VERSION, MATCH, OFFLINE_MAPS, mapOfflineOk, mapNoBots, mapBossOk,
} from './config.js';
import { Level } from './world/level.js';
import { MAP_LAYOUTS } from './world/maps.js';
import { PaintSystem } from './world/paint.js';
import { createLevelMaterial, setLevelLamps } from './world/levelMaterial.js';
import { SwimWake } from './fx/swimWake.js';
import { Decor } from './world/decor.js';
import { createMuralTexture } from './world/murals.js';
import { layoutThumbSVG } from './world/mapThumb.js';
import { dressingFor } from './world/dressing.js';
import { inMode, layoutFor, variantKey } from './world/variants.js';
import './game/kits/index.js';   // kit weapons + subs register themselves (game/kits/registry.js)
import { Physics, Hit } from './game/physics.js';
import { NavGraph } from './game/nav.js';
import { Projectiles } from './game/weapons.js';
import { SubSystem } from './game/subs.js';
import { SpecialSystem } from './game/specials.js';
import { CameraRig } from './game/cameraRig.js';
import { Match } from './game/match.js';
import { Minimap } from './game/minimap.js';
import { Showcase, LOB_EMOTES } from './game/showcase.js';
import { ZoneMarks } from './fx/zoneMarks.js';
import { BOSS_MODE } from './boss/bossMode.js';
import { Announcer } from './audio/announcer.js';
import { setLang, t } from './i18n/strings.js';
import { ensureInviteId } from './net/invites.js';

const params = new URLSearchParams(location.search);
// dev-only: ?devstage lets an online-only stage (config onlineOnly — Cargo Terminal) boot as the backdrop and be walked
// solo offline (never with bots); without it such a stage only ever loads for an online match
const DEV_STAGE = params.has('devstage');
const nextFrame = () => new Promise((r) => requestAnimationFrame(() => r()));
const REFRESH_RATES = [30, 48, 50, 60, 75, 90, 100, 120, 144, 165, 240]; // common display rates (Hz)

// ------------------------------------------------------------------------------------------ persistence
function loadJSON(key, def) { try { const v = JSON.parse(localStorage.getItem(key)); return v ? { ...def, ...v } : { ...def }; } catch { return { ...def }; } }
function saveJSON(key, v) {
  try {
    let existing = {};
    try { existing = JSON.parse(localStorage.getItem(key) || '{}'); } catch {}
    const merged = (v && typeof v === 'object' && !Array.isArray(v)) ? { ...existing, ...v } : v;
    localStorage.setItem(key, JSON.stringify(merged));
  } catch { /* private mode */ }
}
// Menu backdrop: Marina at dusk, west quay. You stand on the deck edge, the water and the low sun behind you.
// (x, z = where you stand; cam* = the camera's offset from you; the camera looks at your chest)
const MENU_SPOT = { x: -21.8, z: -31, camDx: 3.4, camDz: 0.1, camH: 0.95, lookH: 0.98, turn: 0.08, shift: 0.0 };
const DEFAULT_PROFILE = { name: 'Player', level: 1, xp: 0, wins: 0, matches: 0, kills: 0, deaths: 0, totalTurf: 0, weapon: 'shooter', morph: 'slime', coins: 250, musicDisc: 'neon_tide', inventory: ['shooter', 'morph_slime', 'disc_neon_tide', 'disc_spazz'] };

// load = () => import('./literal/path.js'): a literal path so the production build (tools/build-prod.mjs) can bundle it
async function loadModule(load, stubName) {
  try { return await load(); }
  catch (e) {
    console.error(`[slimeduel] failed to load ${String(load).match(/import\(['"]([^'"]+)/)?.[1] || 'a module'} — using stub`, e);
    const stubs = await import('./dev/stubs.js');
    return stubName ? stubs : {};
  }
}

class Game {
  async boot() {
    const t0 = performance.now();
    // real top-down thumbnails for the stage cards, generated from each layout's geometry
    for (const m of MAPS) { try { m.thumb = layoutThumbSVG(MAP_LAYOUTS[m.layout || m.id], m.theme); } catch (e) { console.warn('thumb', m.id, e); } }
    this.settings = G.settings = loadJSON('slimeduel.settings', DEFAULT_SETTINGS);
    setLang(this.settings.lang || 'en');   // before any menu renders (src/i18n/strings.js)
    document.title = t('SLIME DUEL ARENA');
    // desktop app: the window's fullscreen state is owned by the native shell; mirror it into settings for the menu
    if (window.slimeduelNative) {
      this.settings.fullscreen = window.slimeduelNative.isFullScreen();
      window.slimeduelNative.onFullScreenChange((on) => { this.settings.fullscreen = on; });
    }
    // v1.1: fov became horizontal — migrate old vertical values once
    if (this.settings.fovMode !== 'h') { this.settings.fov = DEFAULT_SETTINGS.fov; this.settings.fovMode = 'h'; saveJSON('slimeduel.settings', this.settings); }
    this.profile = loadJSON('slimeduel.profile', DEFAULT_PROFILE);
    if (WEAPON_SUCCESSOR[this.profile.weapon]) this.profile.weapon = WEAPON_SUCCESSOR[this.profile.weapon];   // retired weapons
    if (!WEAPONS[this.profile.weapon]) this.profile.weapon = 'shooter';   // validate weapon key
    // v1.2: the Bald hair style (index 15) was removed, so Rugrat moved 16 → 15. Bald looks fall back to Buzz; owned
    // hair_16 becomes hair_15 (Bald owners keep a hair_15, now Rugrat). Once.
    if (!this.profile.hairV) {
      const p = this.profile, st = p.style;
      if (st && st.hair === 15) st.hair = 14; else if (st && st.hair === 16) st.hair = 15;
      if (Array.isArray(p.inventory) && p.inventory.includes('hair_16')) p.inventory = [...new Set(p.inventory.map((k) => (k === 'hair_16' ? 'hair_15' : k)))];
      p.hairV = 2;
      saveJSON('slimeduel.profile', p);
    }
    const app = document.getElementById('app');
    this.uiRoot = document.getElementById('ui-root');
    this.fadeEl = document.getElementById('fade');

    // UI first so the loading screen shows immediately
    const [menusMod, hudMod] = await Promise.all([loadModule(() => import('./ui/menus.js')), loadModule(() => import('./ui/hud.js'))]);
    this.menus = G.menus = menusMod.Menus ? new menusMod.Menus(this.uiRoot, this._menuApi()) : null;
    if (!params.has('autostart')) ensureInviteId().catch(() => {});   // invite rewards: the server counts online matches from the first one
    this.hud = G.hud = hudMod.HUD ? new hudMod.HUD(this.uiRoot, { playSound: (n, o) => G.audio?.play(n, o) }) : null;
    this._hudMod = hudMod;   // kept so a language switch can rebuild the HUD in the new language (_rebuildHud)
    // map diorama pins/finish live inside the HUD layer (under every other HUD element)
    try { const { DioramaOverlay } = await import('./ui/diorama.js'); this.diorama = new DioramaOverlay(this.hud ? this.hud.el : this.uiRoot); } catch (e) { console.error('[slimeduel] diorama', e); this.diorama = null; }
    this.hud?.setVisible(false);
    this.menus?.show('loading');
    this.bootMarks = [];
    const progress = async (p, label) => { this.bootMarks.push([label, Math.round(performance.now() - t0)]); this.menus?.setLoading(p, label); await nextFrame(); };
    await progress(0.05, 'Mixing slime…');

    // renderer / scene
    this.R = new Renderer(app, this.settings);
    G.renderer = this.R.renderer;
    const scene = (G.scene = new THREE.Scene());
    const camera = (G.camera = new THREE.PerspectiveCamera(this.settings.fov, innerWidth / innerHeight, 0.15, 6500));
    camera.position.set(0, 40, -60);
    this.R.setScene(scene, camera);
    this.input = G.input = new Input(this.R.renderer.domElement);
    this.input.onKey = (e, repeat) => this._onKey(e, repeat);
    this.input.onUnlock = () => this._onPointerUnlock();
    // Click/pointerdown anywhere on the game canvas re-acquires pointer lock during live match
    const tryLock = () => {
      this._ensureAudio();
      if (G.mode === 'match' && this.match && !this.match.paused && (!this.menus || !this.menus.current)) {
        this._relock = false;
        this.input.requestLock();
      }
    };
    this.R.renderer.domElement.addEventListener('mousedown', tryLock);
    this.R.renderer.domElement.addEventListener('pointerdown', tryLock);
    window.addEventListener('click', (e) => {
      this._ensureAudio();
      if (G.mode === 'match' && this.match && !this.match.paused && (!this.menus || !this.menus.current)) {
        if (!e.target.closest('.iw-btn, .iw-modal, .iw-screen, .iw-pal-swatch, input, button, select, [data-interactive]')) {
          tryLock();
        }
      }
    });

    // modules built by other authors
    const [charMod, fxMod, envMod, audioMod, musicMod] = await Promise.all([
      loadModule(() => import('./game/character.js'), true), loadModule(() => import('./fx/fx.js'), true), loadModule(() => import('./world/environment.js'), true),
      loadModule(() => import('./audio/audio.js'), true), loadModule(() => import('./audio/music.js'), true),
    ]);
    this.CharacterClass = charMod.Character;
    try { this.PropKit = (await import('./world/props.js')).PropKit; } catch (e) { console.error('[slimeduel] prop kit failed to load', e); this.PropKit = null; }
    G.audio = audioMod.audio; G.music = musicMod.music;
    this.announcer = G.announcer = new Announcer();
    await progress(0.15, 'Building the plaza…');

    // world
    // (old ?map=sunset links = Tidewater at dusk)
    const pm = params.get('map') === 'sunset' ? 'tidewater' : params.get('map');
    let map = MAPS.find((m) => m.id === pm) || MAPS.find((m) => m.id === 'halyard') || MAPS[0];
    if (!mapOfflineOk(map.id) && !DEV_STAGE) { console.info(`[slimeduel] ${map.name} is online only — booting ${OFFLINE_MAPS[0].name}`); map = OFFLINE_MAPS[0]; }
    this.time = params.get('time') === 'dusk' || params.get('map') === 'sunset' ? 'dusk' : (this.settings.timeOfDay === 'dusk' ? 'dusk' : 'day');
    this.theme = mapTheme(map, !params.has('map') && !params.has('time') && !params.has('autostart') ? 'dusk' : this.time);
    const q = QUALITY[this.settings.quality] || QUALITY.high;
    this.murals = await createMuralTexture();
    try {
      const { createTextureLibrary } = await import('./world/texlib.js');
      this.texlib = await createTextureLibrary(G.renderer, { size: q.paintAtlas >= 4096 ? 512 : 256 });
    } catch (e) { console.error('[slimeduel] texture library failed — procedural fallback', e); this.texlib = null; }
    await this._buildWorld(map);
    this.zoneMarks = new ZoneMarks(scene);   // Zone Control ground markings: build / clear themselves on 'match:state'
    await progress(0.4, 'Filling the harbor…');
    const B = G.level.bounds;
    G.env = new envMod.Environment(G.renderer, scene, { bounds: B, theme: this.theme, shadowSize: q.shadowSize, footprint: this._footprint(G.level) });
    if (G.env.envMap) scene.environment = G.env.envMap;
    // sky-fill balance (scene.environmentIntensity, hemisphere) + per-theme exposure are the environment theme's job
    // (Environment.setTheme), so a stage/time looks the same booted into or switched to mid-session
    G.renderer.toneMappingExposure = 0.94;
    await progress(0.55, 'Teaching squids to swim…');
    G.projectiles = new Projectiles(scene);
    G.subs = new SubSystem(scene);
    G.specials = new SpecialSystem(scene);
    G.fx = new fxMod.FX(scene, { quality: q });
    G.fx.setLighting?.(G.env.getSkyColors?.());
    this._applyNight();
    G.fx.setCollider?.((from, to) => { const h = G.physics.segment(from, to, this._fxHit || (this._fxHit = new Hit()), true); return h.hit ? { point: h.point, normal: h.normal } : null; });
    G.fx.onDropletLand = (point, normal, color, size) => {
      if (G.netm) return;   // online: turf only comes from replicated splats, never from local-only cosmetic droplets
      const team = this._teamOfColor(color);
      if (team < 0) return;
      G.paint.splat(this._tmpV.copy(point).addScaledVector(normal, 0.05), clamp(size * 2.4, 0.12, 0.45), team, { seed: Math.random() });
    };
    this._tmpV = new THREE.Vector3(); this._tmpC = new THREE.Color();
    this.rig = new CameraRig(camera);
    G.post = this.R; G.game = this; G.rig = this.rig;
    // optional modules owned by the VFX / screen-FX work streams (absent = skipped)
    try { const m = await import('./fx/fxHooks.js'); this.fxHooks = m.initFxHooks?.(G) || null; } catch (e) { if (!/Failed to fetch|Cannot find module|404/i.test(String(e))) console.error('[slimeduel] fxHooks', e); }
    try { const m = await import('./fx/screenfx.js'); this.screenfx = m.ScreenFX ? new m.ScreenFX(this.R, G) : null; } catch (e) { if (!/Failed to fetch|Cannot find module|404/i.test(String(e))) console.error('[slimeduel] screenfx', e); }
    this.showcase = new Showcase(G.renderer, this.CharacterClass);
    await progress(0.7, 'Tuning the tentacles…');

    this._setPalette(this._pickPalette());
    this._bindEvents();
    this._startAttract();
    // warm up: compile every shader now so the first shot/splat never hitches
    await progress(0.85, 'Warming up…');
    this._warmup();
    // compile in parallel (KHR_parallel_shader_compile) so the loading screen keeps animating instead of freezing
    try { await G.renderer.compileAsync(scene, camera); } catch { G.renderer.compile(scene, camera); }
    for (const m of this._warmMeshes || []) { G.scene.remove(m); }
    this._warmMeshes?.[0]?.geometry.dispose(); this._warmMeshes = null;
    await progress(0.93, 'Warming up…');
    for (let i = 0; i < 3; i++) { this._frame(1 / 60); await nextFrame(); }
    await progress(1, 'Ready!');
    await new Promise((r) => setTimeout(r, 250));

    this.timer = new THREE.Timer(); this.timer.connect?.(document);
    this.fpsAcc = 0; this.fpsN = 0; this.fps = 60;
    G.mode = 'menu';
    installNet();
    this.menus?.show(params.has('skipTitle') ? 'main' : 'title');
    // the online hub / lobby set loads in the background once the menus are idle (no arena flash on the first visit)
    if (!params.has('autostart')) setTimeout(() => { if (G.mode === 'menu') this.showcase.preloadLobby?.(); }, 2500);
    this._applyAudioVolumes();
    requestAnimationFrame((t) => this._loop(t));
    if (params.has('autostart')) {
      const pm = params.get('mode');
      this.api.startMatch({ mapId: map.id, difficulty: params.get('difficulty') || this.settings.difficulty, duration: +params.get('autostart') || undefined, mode: pm === 'boss' || pm === 'zones' || pm === 'duel' || pm === 'deathmatch' ? pm : 'turf' });
    }
    this.bootMs = Math.round(performance.now() - t0);
    window.__slimeduel = this; // debug/audit hook
    window.__G = G;
    this.debug = {
      endMatch: (t = 0.5) => { if (this.match && !this.match.attract) this.match.time = t; },
      paintRandom: (n = 400) => { const v = new THREE.Vector3(); for (let i = 0; i < n; i++) { v.set((Math.random() - 0.5) * 48, 0.4, (Math.random() - 0.5) * 86); G.paint.splat(v, 0.8 + Math.random() * 1.4, Math.random() < 0.5 ? 0 : 1); } },
      // deterministic stepping for audits: freeze(), then step(ms) advances the sim at a fixed 60 Hz and renders once
      freeze: () => { this.frozen = true; },
      unfreeze: () => { this.frozen = false; this.timer.update(this._lastTs); },
      step: (ms = 16.7) => {
        const n = Math.max(1, Math.round(ms / (1000 / 60)));
        this._skipRender = true;
        for (let i = 0; i < n - 1; i++) this._frame(1 / 60);
        this._skipRender = false;
        this._frame(1 / 60);
      },
      key: (code, down) => { if (down) { this.input.keys.add(code); this.input.pressed.add(code); } else this.input.keys.delete(code); },
      fire: (on) => { this.input.mouse.left = on; },
      freezeBots: () => { for (const a of G.actors) if (a.bot && !a.isLocal) a.bot.update = () => { a.intent.move.set(0, 0, 0); a.intent.fire = false; }; },
    };
  }

  // Build (or rebuild) everything that depends on the stage layout: level, collision, paint atlas, surface material,
  // decor, navigation graph and minimap. Environment/FX/projectiles persist across stages.
  // mode: 'turf' | 'zones' — a stage with Zone Control-only pieces (variants.js) builds a separate world for that mode
  async _buildWorld(map, mode = 'turf') {
    const scene = G.scene;
    const layoutId = map.layout || map.id;
    const worldKey = variantKey(layoutId, MAP_LAYOUTS[layoutId], dressingFor(layoutId), mode);
    if (this.worldKey === worldKey) { this.mapDef = map; return; }
    // the rebuild awaits (lightmap fetch) between swapping G.level and G.paint: hold the simulation until every
    // stage-dependent system matches, or a frame in between raycasts the new level and samples the old paint atlas
    this._building = true;
    try { await this._buildWorldNow(map, scene, layoutId, mode, worldKey); } finally { this._building = false; }
  }
  async _buildWorldNow(map, scene, layoutId, mode = 'turf', worldKey = layoutId) {
    this.zoneMarks?.clear();   // zone markings belong to the old stage's faces
    if (this.levelMesh) { scene.remove(this.levelMesh, this.grateMesh); this.levelMesh.geometry.dispose(); this.grateMesh?.geometry.dispose(); this.levelMat.dispose(); this.grateMat?.dispose(); }
    if (this.decor) { scene.remove(this.decor.group); }
    if (this.props) { this.props.dispose?.(); this.props = null; }
    G.paint?.dispose();
    this.layoutId = layoutId; this.worldKey = worldKey;
    this.mapDef = map;
    const q = QUALITY[this.settings.quality] || QUALITY.high;
    // set dressing first: solid props hand back collision boxes that become part of the level (physics, nav, paint)
    const colliders = [];
    if (this.PropKit) {
      try {
        this.props = new this.PropKit(scene, { castShadow: true, quality: this.settings.quality });
        for (const it of dressingFor(layoutId)) {
          if (!inMode(it, mode)) continue;
          const r = this.props.add(it.type, it);
          if (r && r.colliders) colliders.push(...r.colliders);
        }
        this.props.build();
      } catch (e) { console.error('[slimeduel] props failed', e); this.props = null; }
    }
    const level = (G.level = new Level(layoutFor(MAP_LAYOUTS[layoutId], mode), colliders));
    G.physics = new Physics(level);
    const lightmap = await this._loadLightmap(level, worldKey);
    G.paint = new PaintSystem(G.renderer, level, { atlasSize: q.paintAtlas, maxDensity: q.paintAtlas >= 4096 ? 30 : 18 });
    this.murals.userData.setStage?.(layoutId);   // stage decals (murals.js) before the material reads the table
    this.levelMat = createLevelMaterial(G.paint.texture, G.paint.size, this.murals, { lightmap, texlib: this.texlib });
    (this.swimWake || (this.swimWake = new SwimWake())).reset();
    this.levelMesh = new THREE.Mesh(level.buildGeometry(G.paint.size), this.levelMat);
    this.levelMesh.castShadow = true; this.levelMesh.receiveShadow = true;
    this.levelMesh.name = 'level';
    scene.add(this.levelMesh);
    // grates: same surface shader, cut-out holes, no ink (they cast no shadow; the mesh is too fine for the shadow map)
    this.grateMat = createLevelMaterial(G.paint.texture, G.paint.size, this.murals, { grate: true, lightmap, texlib: this.texlib });
    const gg = level.buildGeometry(G.paint.size, (b) => b.grate);
    this.grateMesh = new THREE.Mesh(gg, this.grateMat);
    this.grateMesh.receiveShadow = true; this.grateMesh.visible = gg.index.count > 0;
    scene.add(this.grateMesh);
    this.decor = new Decor(scene, level);
    G.nav = new NavGraph(level, G.physics);
    this.minimap = new Minimap(level, G.paint);
    if (G.env?.rebuildForArena) G.env.rebuildForArena(level.bounds, this._footprint(level));
    else if (G.env?.setFootprint) G.env.setFootprint(this._footprint(level));
    if (G.teamColors[0]) this._setPalette(this.palette || this._pickPalette());
  }

  // Baked AO (tools/bake-ao.mjs). Applied only when the bake matches this exact layout.
  async _loadLightmap(level, layoutId) {
    try {
      const meta = await (await fetch(`assets/lightmaps/${layoutId}.json`, { cache: 'no-cache' })).json();
      level.layoutLightmap(meta.ppm, meta.size);
      if (level.layoutHash !== meta.hash) { console.warn(`[slimeduel] lightmap for ${layoutId} is stale — re-run tools/bake-ao.mjs`); level.lightSize = 0; for (const f of level.faces) f.light = null; return null; }
      const tex = await new THREE.TextureLoader().loadAsync(`assets/lightmaps/${layoutId}.png?h=${meta.hash}`);
      tex.colorSpace = THREE.NoColorSpace;
      tex.generateMipmaps = true; tex.minFilter = THREE.LinearMipmapLinearFilter; tex.magFilter = THREE.LinearFilter;
      tex.anisotropy = 4;
      return tex;
    } catch (e) {
      console.warn('[slimeduel] no lightmap for', layoutId, e.message);
      for (const f of level.faces) f.light = null;
      return null;
    }
  }

  // deck slabs (top at deck level, bottom in the sea) for the environment: axis-aligned boxes and boxes turned about Y
  // (obox) as oriented rects — centre (cx, cz), half extents (hx, hz), local x axis (ax, az) — plus their XZ AABB
  _footprint(level) {
    return level.blocks.filter((b) => (b.aligned || Math.abs(b.axes[1].y) > 0.9999) && b.aabbMax.y < 0.01 && b.aabbMax.y > -2.5 && b.aabbMin.y < -1)
      .map((b) => ({ minX: b.aabbMin.x, maxX: b.aabbMax.x, minZ: b.aabbMin.z, maxZ: b.aabbMax.z,
        ...(b.aligned ? {} : { cx: b.center.x, cz: b.center.z, hx: b.half.x, hz: b.half.z, ax: b.axes[0].x, az: b.axes[0].z }) }));
  }

  _warmup() {
    // trigger one of each effect off-screen so shaders + pools exist
    const p = new THREE.Vector3(0, -30, 0), n = new THREE.Vector3(0, 1, 0);
    const c = G.teamColors[0];
    try {
      G.fx.burst(p, n, c, { count: 4 }); G.fx.ring(p, n, c, {}); G.fx.explosion(p, c, 2); G.fx.splatted(p, c);
      G.fx.wake(p, n, c, 5); G.fx.muzzle(p, n, c); G.fx.spawnFlash(p, c);
    } catch (e) { console.warn('fx warmup', e); }
    // materials made lazily on first use (a thrown bomb's) get an off-screen stand-in so the boot compile covers them
    try {
      const g = new THREE.SphereGeometry(0.1, 8, 6);
      this._warmMeshes = [0, 1].map((t) => { const m = new THREE.Mesh(g, G.projectiles._bombMat(t)); m.position.copy(p); m.castShadow = true; G.scene.add(m); return m; });
    } catch (e) { console.warn('bomb warmup', e); }
  }

  // ---------------------------------------------------------------------------------------- palette
  // the room's colours while you're in one (so the lobby line-up and the match agree on every screen)
  _pickPalette() {
    if (this.settings.colorblind) return COLORBLIND_PALETTE;
    const room = G.net && G.net.state !== 'offline' && G.net.state !== 'error' && TEAM_PALETTES[G.net.lobby?.palette];
    if (room) return room;
    const savedSlime = this.profile?.slimeColor || (() => { try { return JSON.parse(localStorage.getItem('slimeduel.profile') || '{}').slimeColor; } catch { return null; } })();
    if (savedSlime) {
      const slimeHex = String(savedSlime).toLowerCase();
      const matchPal = TEAM_PALETTES.find(p => p.a.toLowerCase() === slimeHex);
      if (matchPal) return matchPal;
      const matchPalB = TEAM_PALETTES.find(p => p.b.toLowerCase() === slimeHex);
      if (matchPalB) return { id: matchPalB.id + '-rev', a: matchPalB.b, b: matchPalB.a, names: [matchPalB.names[1], matchPalB.names[0]] };
      const savedRival = this.profile?.rivalColor || (() => { try { return JSON.parse(localStorage.getItem('slimeduel.profile') || '{}').rivalColor; } catch { return '#e02cd8'; } })() || '#e02cd8';
      return { id: 'custom-slime', a: savedSlime, b: savedRival, names: ['Lime', 'Magenta'] };
    }
    const greenPink = TEAM_PALETTES.find(p => p.id === 'lime-magenta') || TEAM_PALETTES[0];
    return greenPink;
  }
  paletteIndex() {
    const savedSlime = this.profile?.slimeColor || (() => { try { return JSON.parse(localStorage.getItem('slimeduel.profile') || '{}').slimeColor; } catch { return null; } })();
    if (savedSlime) {
      const slimeHex = String(savedSlime).toLowerCase();
      const i = TEAM_PALETTES.findIndex(p => p.a.toLowerCase() === slimeHex);
      if (i >= 0) return i;
      const ib = TEAM_PALETTES.findIndex(p => p.b.toLowerCase() === slimeHex);
      if (ib >= 0) return ib;
    }
    const i = TEAM_PALETTES.indexOf(this.palette);
    return i >= 0 ? i : 0;
  }
  _roomPalette(l) {
    if (this.settings.colorblind || G.mode === 'match' || !l) return;
    const p = TEAM_PALETTES[l.palette];
    if (p && p !== this.palette) this._setPalette(p);
  }
  _setPalette(p) {
    this.palette = p;
    G.teamHex = [p.a, p.b];
    G.teamColors = [new THREE.Color(p.a), new THREE.Color(p.b)];
    this.levelMat.userData.uniforms.uTeamA.value.copy(G.teamColors[0]);
    this.levelMat.userData.uniforms.uTeamB.value.copy(G.teamColors[1]);
    if (this.grateMat) { this.grateMat.userData.uniforms.uTeamA.value.copy(G.teamColors[0]); this.grateMat.userData.uniforms.uTeamB.value.copy(G.teamColors[1]); }
    // warm/low light mutes saturated ink: give it more self-glow at dusk so team colours stay the loudest thing on screen
    // ink self-light: more at dusk, a touch more in golden hour's long shadows
    this.levelMat.userData.uniforms.uInkGlow.value = { sunset: 0.2, golden: 0.1 }[this.theme] ?? 0.07;
    this.decor.setTeamColors(G.teamColors);
    this.props?.setTeamColors?.(G.teamColors[0], G.teamColors[1]);
    G.projectiles.refreshColors();
    for (const a of G.actors) a.character.setColor(G.teamColors[a.team]);
    this.minimap.version = -1;
    this.menus?.setAccent?.(p.a, p.b);
  }
  _teamOfColor(color) {
    const c = color.isColor ? color : this._tmpC.set(color);
    for (let t = 0; t < 2; t++) { const k = G.teamColors[t]; if (Math.abs(k.r - c.r) + Math.abs(k.g - c.g) + Math.abs(k.b - c.b) < 0.05) return t; }
    return -1;
  }

  // ---------------------------------------------------------------------------------------- menus api
  _menuApi() {
    const self = this;
    const api = (this.api = {
      version: VERSION,
      weapons: WEAPONS, weaponOrder: WEAPON_ORDER, specials: SPECIALS, specialOrder: SPECIAL_ORDER, sub: SUB.bomb, subs: SUBS, subOrder: SUB_ORDER, maps: MAPS, difficulties: DIFFICULTY,
      getSettings: () => ({ ...self.settings }),
      setSettings: (partial) => self._setSettings(partial),
      getProfile: () => {
        let cur = {};
        try { cur = JSON.parse(localStorage.getItem('slimeduel.profile') || '{}'); } catch {}
        const p = { ...self.profile, ...cur };
        self.profile = p;
        return { ...p, played: p.matches, xpToNext: PROGRESSION.xpForLevel(p.level) };
      },
      setProfileName: (n) => { self._syncProfile(); self.profile.name = String(n || 'Player').slice(0, 16); saveJSON('slimeduel.profile', self.profile); },
      // locker look ({ hair, skin, outfit, eyes, hat, brows, … } — indices into character-style.js tables)
      setProfileStyle: (st) => { self._syncProfile(); self.profile.style = { ...(st || {}) }; saveJSON('slimeduel.profile', self.profile); },
      getLoadout: () => {
        let cur = {};
        try { cur = JSON.parse(localStorage.getItem('slimeduel.profile') || '{}'); } catch {}
        const morph = cur.morph || self.profile.morph || 'squid';
        const weapon = cur.weapon || self.profile.weapon || 'shooter';
        return {
          weapon: WEAPONS[weapon] ? weapon : 'shooter',
          sub: self._subFor(weapon),
          special: self._specialFor(weapon),
          morph: String(morph).replace(/^morph_/, '').toLowerCase()
        };
      },
      setLoadout: ({ weapon, sub, special, morph }) => {
        self._syncProfile();   // pick up what the menus saved straight to storage (purchases, gems) before writing it back
        if (weapon !== undefined) {
          if (!WEAPONS[weapon]) return;
          self.profile.weapon = weapon;
        }
        if (sub !== undefined) self.profile.sub = SUBS[sub] ? sub : null;
        if (special !== undefined) self.profile.special = SPECIALS[special] ? special : null;
        if (morph !== undefined) self.profile.morph = String(morph || 'squid').replace(/^morph_/, '').toLowerCase();
        saveJSON('slimeduel.profile', self.profile);
        self._applyPracticeLoadout();   // in practice the new kit is in your hands straight away
      },
      setMorph: (m) => {
        self._syncProfile();
        const clean = String(m || 'squid').replace(/^morph_/, '').toLowerCase();
        self.profile.morph = clean;
        saveJSON('slimeduel.profile', { ...self.profile, morph: clean });
        self._applyPracticeLoadout();
      },
      startMatch: (o) => self.startMatch(o),
      // practice: solo on a random stage (no enemies, no clock); the loadout can be changed mid-session
      startPractice: (o) => self.startPractice(o),
      isPractice: () => self._inPractice(),
      practiceInfo: () => (self._inPractice() ? { map: self.mapDef.name, weapon: self.match.local?.weaponId, sub: self.match.local?.subId, special: self.match.local?.specialId } : null),
      practiceReset: () => self.practiceReset(),
      practiceNewStage: () => self.startPractice(),
      quitPractice: () => self.quitToMenu('loadout'),
      resumeMatch: () => self.resume(),
      // online results: the host can take the room back to the lobby without waiting out the timer
      netBackToLobby: () => { if (G.netm && G.net?.isHost && self.match?.state === 'results') { clearTimeout(self._netEndT); G.netm.sendEnd(); self.netMatchEnd(); } },
      quitMatch: () => self.quitToMenu(),
      rematch: () => self.startMatch(self.lastMatchOpts || {}),
      toMainMenu: () => self.quitToMenu(),
      onScreenChange: (s) => self._onScreen(s),
      playSound: (n) => { G.audio?.init?.(); G.audio?.play(n); },
      getPalettes: () => TEAM_PALETTES,
      setPalette: (idxOrId) => {
        const pal = typeof idxOrId === 'number' ? TEAM_PALETTES[idxOrId] : TEAM_PALETTES.find(p => p.id === idxOrId);
        if (pal) self._setPalette(pal);
      },
    });
    return api;
  }

  // the loadout's sub: the one picked in the loadout, else the weapon's own default
  _subFor(weapon) { return (SUBS[this.profile.sub] && KU.ownsSub(this.profile, this.profile.sub) && this.profile.sub) || (WEAPONS[weapon || 'shooter'] || WEAPONS.shooter).sub || 'bomb'; }
  // the loadout's special: the one picked in the loadout, else the weapon's own
  _specialFor(weapon) { return (SPECIALS[this.profile.special] && KU.ownsSpecial(this.profile, this.profile.special) && this.profile.special) || (WEAPONS[weapon || 'shooter'] || WEAPONS.shooter).special || 'slam'; }

  _setSettings(partial) {
    Object.assign(this.settings, partial);
    saveJSON('slimeduel.settings', this.settings);
    if ('lang' in partial) { setLang(partial.lang); document.title = t('SLIME DUEL ARENA'); this._rebuildHud(); }
    if ('quality' in partial || 'shadows' in partial || 'bloom' in partial) this.R?.applySettings(this.settings);
    if ('fullscreen' in partial && window.slimeduelNative) window.slimeduelNative.setFullScreen(!!partial.fullscreen);
    if ('master' in partial || 'music' in partial || 'sfx' in partial || 'announcer' in partial) {
      this._applyAudioVolumes();
      if ((this.settings.announcer ?? 0.85) <= 0.005 || (this.settings.master ?? 0.8) <= 0.005) this.announcer?.stop?.();
    }
    if ('colorblind' in partial && G.mode !== 'match') this._setPalette(this._pickPalette());
  }
  _applyAudioVolumes() { G.audio?.setVolumes?.({ master: this.settings.master, music: this.settings.music, sfx: this.settings.sfx, announcer: this.settings.announcer }); }

  // The HUD's static labels are built once with the current language; a language switch rebuilds it (menus only —
  // mid-match the old HUD stays until the next load, which is the safer trade). The TAB-map diorama mounts inside
  // the HUD layer, so it is recreated with it.
  async _rebuildHud() {
    if (G.mode === 'match' || !this._hudMod?.HUD) return;
    try {
      this.diorama?.el?.remove();
      this.hud?.dispose();
      this.hud = G.hud = new this._hudMod.HUD(this.uiRoot, { playSound: (n, o) => G.audio?.play(n, o) });
      try { const { DioramaOverlay } = await import('./ui/diorama.js'); this.diorama = new DioramaOverlay(this.hud ? this.hud.el : this.uiRoot); } catch (e) { console.error('[slimeduel] diorama', e); this.diorama = null; }
      this.hud?.setVisible(false);
    } catch (e) { console.error('[slimeduel] hud rebuild', e); }
  }

  _onScreen(s) {
    // the loadout opened mid-practice sits over the live stage: tuck the HUD away while it's up
    if (G.mode === 'match' && this.hud) {
      const hide = s === 'loadout';
      if (hide !== !!this._hudTucked) { this._hudTucked = hide; this.hud.setVisible(!hide); }
    } else this._hudTucked = false;
    if (!this.showcase) return;
    if (s === 'loadout') { if (this.showcase.mode) this.showcase.hide(); }
    else if (s !== 'results') { if (this.showcase.mode === 'loadout') this.showcase.hide(); }
    if (G.mode === 'menu') {
      if (s === 'title' || s === 'main' || s === 'setup' || s === 'settings' || s === 'howto' || s === 'credits' || s === 'loadout' || s === 'locker' || s === 'shop' || s === 'leaderboard' || s === 'rewards' || s === 'online' || s === 'onlinemode' || s === 'lobby') {
        const targetTrack = s === 'title' ? 'title' : this._menuMusic();
        if (this._musicTrack !== targetTrack) this._playMusic(targetTrack);
      }
    }
  }
  _menuMusic() {
    // retired discs (Gang Gang, Beats, Synth) fall back to the new default
    const d = this.profile?.musicDisc;
    return !d || d === 'gang_gang' || d === 'menu' || d === 'title' ? 'neon_tide' : d;
  }
  _ensureAudio() {
    if (!this._audioOn) {
      this._audioOn = true;
      G.audio?.init?.();
      this._applyAudioVolumes();
      if (G.mode !== 'match') {
        const targetTrack = this.menus?.current === 'title' || !this.menus ? 'title' : this._menuMusic();
        this._playMusic(targetTrack);
      }
    }
  }
  _playMusic(t) {
    if (G.mode === 'match' && (t === 'menu' || t === 'title' || t === this._menuMusic())) return;
    if (t === 'menu') t = this._menuMusic();
    this._musicTrack = t;
    try {
      G.music?.play(t, { fade: 1.2 });
      // asking for the track that's already current is a no-op — if it was left paused, un-pause it (unless we're paused)
      if (t && !this.match?.paused) G.music?.resume?.();
    } catch (e) { /* not initialised yet */ }
  }

  // ---------------------------------------------------------------------------------------- input routing
  _onKey(e, repeat) {
    // first gesture unlocks audio
    this._ensureAudio();
    if (G.mode === 'match' && this.match && !this.match.paused && !this.menus?.current) {
      if (e.code === 'Escape' && this.hud?.emoteOpen) { this.hud.closeEmoteWheel(true); return true; }
      if (e.code === 'KeyP' && this._spectating) { if (!repeat) this._specPov = !this._specPov; return true; }   // spectate: player view
      if (G.netm && (e.code === 'Escape' || e.code === 'KeyP')) {
        // online never pauses: the first Esc only frees the mouse (the match runs on); Esc again opens the match menu
        if (e.code === 'KeyP') return true;
        if (document.pointerLockElement || performance.now() - (this._unlockT || 0) < 300) { this.input.exitLock(); this._showMouseFree(); }
        else this.pause();
        return true;
      }
      if (e.code === 'Escape' || e.code === 'KeyP') { this.pause(); return true; }
      if (e.code === 'KeyL' && this.match.practice && !repeat) { this.openPracticeLoadout(); return true; }
      return false;
    }
    if (this.menus && this.menus.current) return this.menus.handleKey(e) || false;
    return false;
  }
  _onPointerUnlock() {
    this._relock = true;
    this._unlockT = performance.now();
    // only a live round pauses on focus loss; intro / time's up / judge / results release the mouse on purpose.
    // Holding the map or opening the mid-game Emote Wheel is never a reason to pause: relock on the next click.
    if (this._emoteUnlock || this.hud?.emoteOpen) { this._emoteUnlock = false; return; }
    if (this.match?.controller?.mapHeld || (this.rig?.mapK ?? 0) > 0) return;
    if (G.mode === 'match' && this.match && !this.match.paused && this.match.state === 'playing' && !this.menus?.current) {
      if (G.netm) this._showMouseFree();   // online: no pause, just how to get back in
      else this.pause();
    }
  }
  _showMouseFree() { this._mouseFree = true; this.hud?.setMouseFree?.(true); }
  // pull the fog back while the view is overhead (the stage is ~150 m away up there), restore it exactly after
  _dioFog() {
    const f = G.scene?.fog, k = this.rig.mapK;
    if (!f || !f.isFog) return;
    if (k > 0) {
      if (!this._fog0) this._fog0 = { near: f.near, far: f.far };
      const e = k * k * (3 - 2 * k);
      f.near = this._fog0.near + 190 * e; f.far = this._fog0.far + 600 * e;
    } else if (this._fog0) { f.near = this._fog0.near; f.far = this._fog0.far; this._fog0 = null; }
  }


  // ---------------------------------------------------------------------------------------- events → HUD/audio
  _bindEvents() {
    const self = this;
    let lastHitSnd = 0, lastHurtSnd = 0;
    on('hit', ({ attacker, victim, damage, killed }) => {
      if (!this.match || this.match.attract) return;
      if (attacker?.isLocal) {
        this.hud?.hitMarker(killed ? 'kill' : 'hit');
        if (G.time - lastHitSnd > 0.06) { lastHitSnd = G.time; G.audio?.play('hit_marker', { volume: 0.6 }); }
      }
    });
    on('damage', ({ victim, amount, attacker, source }) => {
      if (!this.match || this.match.attract || !victim.isLocal) return;
      let ang = null;
      if (attacker && attacker !== victim) {
        const v = this._dmgV || (this._dmgV = new THREE.Vector3());
        v.copy(attacker.pos); v.y += 1; v.project(G.camera);
        let dx = v.x, dy = -v.y;
        const behind = v.z > 1;
        if (behind) { dx = -dx; dy = -dy; }
        if (!behind && Math.abs(dx) < 1 && Math.abs(dy) < 1) ang = dx >= 0 ? 0 : Math.PI;   // attacker on screen: ink the nearer side edge, never over them
        else ang = Math.atan2(dy * innerHeight, dx * innerWidth);
      }
      this.hud?.damage(clamp(amount / 80, 0.15, 1), G.teamHex[victim.enemyTeam], ang);
      if (G.time - lastHurtSnd > 0.25) { lastHurtSnd = G.time; G.audio?.play('hurt', { volume: 0.7 }); }
      if (amount >= 40) this.rig.addShake(clamp((amount - 30) / 220, 0, 0.4));   // only heavy hits move the camera; chip damage reads through the HUD
    });
    // a squid dropping back into its own ink (dolphin-jump re-entry, hopping in from dry ground) gets a wet plunge;
    // transform dives already play squid_in
    const formT = new WeakMap();
    on('actor:form', ({ actor }) => formT.set(actor, G.time));
    on('actor:dive', ({ actor, speed }) => {
      if (!actor || !this.match || this.match.attract || G.time - (formT.get(actor) ?? -9) < 0.2) return;
      if (actor.isLocal || actor._nearCamera?.()) G.audio?.play('swim_splash', { pos: actor.isLocal ? undefined : actor.pos, volume: (actor.isLocal ? 0.5 : 0.32) * Math.min(1, 0.55 + (speed || 0) / 16) });
    });
    // online, humans-only stage: a player who left is gone from the match (Match.removeActor) — say so in the feed
    on('actor:removed', ({ actor }) => { if (this.match && !this.match.attract) this.hud?.feed({ text: { id: '{name} left the match', params: { name: t(actor.name) } }, color: G.teamHex[actor.team], kind: 'info' }); });
    on('splatted', ({ victim, attacker, cause }) => {
      if (!this.match || this.match.attract) return;
      this._addDeathMark(victim);
      const local = this.match.local;
      if (attacker?.isLocal) {
        G.audio?.play('splat_enemy', { volume: 0.9 });
        this.hud?.feed({ text: { id: 'You splatted {name}!', params: { name: t(victim.name) } }, color: G.teamHex[local.team], kind: 'kill' });
      } else if (victim.isLocal) {
        G.audio?.play('splatted_self');
        G.audio?.duck?.(0.45, 2.2);
        this.hud?.feed({ text: attacker && attacker !== victim ? { id: 'You were splatted by {by}', params: { by: t(attacker.name) } } : { id: 'You were splatted' }, color: attacker ? G.teamHex[attacker.team] : '#6fd0ff', kind: 'death' });
        // the card shows what did it (hud.js splatCause): the attacker's main weapon, or the sub / special / the sea
        const by = attacker ? attacker.name : cause === 'water' ? null : 'enemy slime';
        this.hud?.showSplatted({ by, byColor: attacker ? G.teamHex[attacker.team] : '#6fd0ff', respawn: PLAYER.respawnTime, attacker: attacker || null, cause });
        this.rig.mode = 'spectate';
        this.rig.spectate = { actor: attacker && attacker.alive ? attacker : null, pos: victim.pos.clone(), from: victim.pos.clone() };
        this.rig.lookAt.copy(victim.pos);
      } else if (victim.team === local?.team) {
        G.audio?.play('ally_splatted', { volume: 0.5 });
        this.hud?.feed({ text: attacker ? { id: '{name} was splatted by {by}', params: { name: t(victim.name), by: t(attacker.name) } } : { id: '{name} was splatted', params: { name: t(victim.name) } }, color: G.teamHex[victim.enemyTeam], kind: 'death' });
      } else if (attacker && attacker.team === local?.team) {
        this.hud?.feed({ text: { id: '{name} splatted {victim}', params: { name: t(attacker.name), victim: t(victim.name) } }, color: G.teamHex[attacker.team], kind: 'ally' });
      } else {   // enemy on enemy (deathmatch is everyone against everyone) or the sea
        this.hud?.feed({ text: attacker ? { id: '{name} splatted {victim}', params: { name: t(attacker.name), victim: t(victim.name) } } : { id: '{name} was splatted', params: { name: t(victim.name) } }, color: G.teamHex[attacker ? attacker.team : victim.team], kind: 'info' });
      }
    });
    on('respawn', ({ actor }) => {
      if (!this.match || this.match.attract) return;
      if (actor.isLocal) {
        this.hud?.hideSplatted(); this.rig.follow(actor, true); this.rig.yaw = actor.yaw; this.rig.pitch = -0.12;
        // a Super Jump planned on the TAB map while splatted launches now (normal charge + flight)
        this.match.controller?.launchQueuedJump?.();
      }
    });
    on('special:ready', ({ actor }) => {
      if (actor.isLocal && !this.match?.attract) { G.audio?.play('special_ready'); }
    });
    on('special:use', ({ actor, id }) => {
      if (actor.isLocal && !this.match?.attract) this.hud?.banner('special', t(SPECIALS[id].name).toUpperCase() + '!');
    });
    on('shake', ({ amount, pos }) => { if (!this.match?.attract) this.rig.addShake(amount, pos); });
    on('recoil', ({ amount }) => { if (!this.match?.attract) this.rig.recoil(amount); });
    on('lowink', ({ actor }) => { if (actor.isLocal) this._lowInkFlash = 1.2; });
    // footsteps (character animation → 'actor:footstep'): surface-aware, only for actors near the camera
    on('actor:footstep', ({ actor, surface, pos, speed }) => {
      if (!actor || !actor.alive || actor.form === 'squid') return;
      const p = pos || actor.pos;
      if (!actor.isLocal && G.camera.position.distanceToSquared(p) > 18 * 18) return;
      const name = surface === 1 ? 'step_ink' : surface === 2 ? 'step_enemy' : 'step_dry';
      const vol = (actor.isLocal ? 0.7 : 0.45) * Math.min(1, 0.45 + (speed || actor.anim.speed || 0) / 8);
      G.audio?.play(name, { pos: actor.isLocal ? undefined : p, volume: vol });
    });
    on('match:oneminute', () => { this.hud?.banner('one_minute'); G.audio?.play('one_minute'); if (this.match?.mode !== 'boss') this._playMusic('battle_final'); });
    on('match:count', ({ n }) => { this.hud?.countdown(n); G.audio?.play('final_count'); });
    on('match:state', ({ state, match }) => {
      if (match.attract || match !== this.match) return;
      if (state === 'intro') this._intro();
      if (state === 'playing') {
        if (!match.practice) { this.hud?.banner('go'); G.audio?.play('go_horn'); }
        if (match.mode !== 'boss') this._playMusic('battle');   // boss mode: the boss audio director scores it by phase
        if (this.match.local) { this.rig.follow(this.match.local, true); }
      }
      if (state === 'finish') {
        const bossWon = match.mode === 'boss' && match.boss?.dead;   // the defeat already had its moment (boss:defeat)
        this.hud?.banner('timesup');
        if (!bossWon) G.audio?.play('times_up');
        if (match.mode !== 'boss') {
          // the final-minute recording is timed to ring out just past the horn; anything else stops here
          if (G.music?.fileTrack !== 'battle_final') G.music?.stop?.(0.4);
          this._musicTrack = null;
        }
        this.input.exitLock();
        if (match.boss) this._bossFinishCam(match.boss);
      }
      if (state === 'judge') this._judge();
    });
  }

  // ---------------------------------------------------------------------------------------- death markers
  // A knocked-out slime blob in the victim's colour where anyone (either team) was splatted, MATCH.deathMarkLife s, fading out at the
  // end. One fixed pool, reused (no per-frame allocation): the HUD draws the world-view and minimap marks from it
  // (positions filled in _updateHud), the TAB map diorama its own (G.deathMarks).
  _deathPool() {
    return this.deathMarks || (this.deathMarks = G.deathMarks = Array.from({ length: 12 }, () => (
      { on: false, id: 0, x: 0, y: 0, z: 0, team: 0, name: '', ally: false, local: false, t: 0, k: 0, sx: 0, sy: 0, sc: 1, vis: false, mx: 0, my: 0 })));
  }
  _addDeathMark(victim) {
    const pool = this._deathPool();
    let d = pool.find((o) => !o.on);
    if (!d) d = pool.reduce((o, b) => (b.t > o.t ? b : o));   // all showing: recycle the oldest
    const p = victim.pos;
    this._dmId = (this._dmId || 0) + 1;
    d.on = true; d.id = this._dmId; d.t = 0; d.k = 1; d.vis = false;
    d.x = p.x; d.y = Math.max(p.y, PLAYER.waterY + 0.1); d.z = p.z;   // lost at sea: on the water where they went in
    d.team = victim.team; d.name = victim.name || ''; d.local = !!victim.isLocal;
  }
  _ageDeathMarks(dt) {
    const life = MATCH.deathMarkLife ?? 5, fade = MATCH.deathMarkFade ?? 1.3;
    for (const d of this._deathPool()) {
      if (!d.on) continue;
      d.t += dt;
      if (d.t >= life) { d.on = false; continue; }
      d.k = d.t > life - fade ? (life - d.t) / fade : 1;
    }
  }
  _clearDeathMarks() { for (const d of this._deathPool()) d.on = false; }

  // ---------------------------------------------------------------------------------------- attract mode
  _startAttract() {
    if (this.match) this.match.dispose();
    this.rig.dioFlip = false;
    G.projectiles.clear(); G.subs.clear(); G.specials.clear(); G.fx.clear?.(); G.paint.clear(); this._clearDeathMarks();
    // the menu backdrop: the bare stage, nobody on it and no slime — the camera just tours it (?devstage stands idle kids
    // there for the render audits)
    const m = (this.match = G.match = new Match({ attract: true, duration: 99999, difficulty: 'easy', CharacterClass: this.CharacterClass, rig: this.rig, input: this.input,
      noBots: true, mannequins: DEV_STAGE }));
    m.setup(); m.start();
    m.frozen = true;
    this.attractT = 0; this.shotT = 0; this.shotIdx = 0;
    this._attractShot();
    this.hud?.setVisible(false);
  }
  // ---- Menu scene: Marina at dusk with you on the quay (main menu only)
  // Marina + its dusk look, whatever stage the last match used. Matches pick their own stage and time when they start.
  async _menuStage() {
    const map = MAPS.find((m) => m.id === 'halyard') || MAPS[0];
    await this._buildWorld(map, 'turf');
    const theme = mapTheme(map, 'dusk');
    if (theme !== this.theme) {
      this.theme = theme;
      G.env.setTheme?.(theme);
      if (G.env.envMap) G.scene.environment = G.env.envMap;
      G.fx.setLighting?.(G.env.getSkyColors?.());
    }
    this._applyNight();
    this.mapDef = map;
  }
  /** (Re)dress the hero in your look, put them on the quay and hold the scenic shot. */
  _showMenuScene(style, colorHex, weapon) {
    const S = MENU_SPOT;
    this._menuSceneOn = true;
    // home has its own street scene: never leave a locker / shop pedestal standing over it
    if (this.showcase && ['locker', 'shop', 'hub'].includes(this.showcase.mode)) this.showcase.hide();
    const prof = this.profile || {};
    style = style || prof.style || { hair: 0, skin: 1 };
    weapon = weapon || prof.weapon || 'shooter';
    const morph = String(prof.morph || 'squid').replace(/^morph_/, '').toLowerCase();
    const sig = JSON.stringify([style, colorHex, weapon, morph, savedCos().skin]);
    if (!this._hero || this._hero.sig !== sig) {
      if (this._hero) { G.scene.remove(this._hero.c.root); this._hero.c.dispose?.(); }
      const c = new this.CharacterClass({ color: new THREE.Color(colorHex || '#a6f01a'), weapon, style: { ...style }, name: 'hero', isLocal: false, morph, weaponSkin: savedCos().skin });
      const a = { time: 0, speed: 0, localMove: { x: 0, z: 0 }, grounded: true, vy: 0, aimPitch: 0, firing: false, charge: 0, rolling: false, form: 'kid', wallNormal: new THREE.Vector3(0, 0, 1), ink: 1, lowInk: false, special: 0, invuln: false };
      c._a = a;
      c.setDance('menu_idle');
      for (let i = 0; i < 14; i++) { a.time += 1 / 30; c.update(1 / 30, a); }   // pre-warm: appears posed, not T-posed
      G.scene.add(c.root);
      this._hero = { c, a, sig, idle: 'menu_idle', idleT: 9, act: null };
    }
    const h = this._hero, hit = this._heroHit || (this._heroHit = new Hit());
    const gy = G.physics.segment(this._tmpV.set(S.x, 20, S.z), this._tmpC2 || (this._tmpC2 = new THREE.Vector3(S.x, -4, S.z)), hit, true).hit ? hit.point.y : 0;
    h.c.root.position.set(S.x, gy, S.z);
    h.c.root.rotation.set(0, Math.atan2(S.camDx, S.camDz) + S.turn, 0);
    h.c.setVisible?.(true); h.c.root.visible = true;
    // aim a little to the camera's right of you, so you stand left of the frame's middle (clear of the lamp post behind)
    const fl = Math.hypot(S.camDx, S.camDz), rx = S.camDz / fl, rz = -S.camDx / fl;
    h.shot = { pos: new THREE.Vector3(S.x + S.camDx, gy + S.camH, S.z + S.camDz), look: new THREE.Vector3(S.x + rx * S.shift, gy + S.lookH, S.z + rz * S.shift) };
    if (this.rig.mode !== 'scenic') this.rig.scenic(h.shot.pos, h.shot.look, { sway: 0.45, speed: 0.08, push: 0.7 });
  }
  _hideMenuScene() {
    this._menuSceneOn = false;
    if (this._hero) { this._hero.c.root.visible = false; this._hero.act = null; }
    this.shotT = 0;   // the attract camera picks its next shot straight away
  }
  // The menus write some profile keys straight to localStorage (shop purchases, gems, equips). Fold them into the
  // in-memory profile before the engine saves it whole, so a stale copy never undoes a purchase or an equip.
  _syncProfile() {
    let cur = null;
    try { cur = JSON.parse(localStorage.getItem('slimeduel.profile') || 'null'); } catch { cur = null; }
    if (cur && typeof cur === 'object') this.profile = { ...this.profile, ...cur };
    return this.profile;
  }
  _menuHeroDance(id) {
    const h = this._hero;
    if (!h || !this._menuSceneOn) return;
    const E = LOB_EMOTES[id] || LOB_EMOTES.booyah;
    h.act = { t: E.dur * 3, cycle: E.dur, ct: E.dur, t0: E.t0 || 0 };   // about three loops of the dance
    h.c.setDance(E.dance || 'victory');
    if (E.v != null) h.c.danceVar = E.v;   // which "victory" it is (pump / flourish / wave)
    h.c.danceT = E.t0 || 0;                // restart even when the last dance was the same kind
    if (E.trig) h.c.trigger(E.trig);
  }
  _updateMenuHero(dt) {
    const h = this._hero;
    if (!h || !this._menuSceneOn) return;
    h.a.time += dt;
    h.c.update(dt, h.a);
    if (h.act) {
      h.act.t -= dt; h.act.ct -= dt;
      if (h.act.t <= 0) { h.act = null; h.c.setDance(h.idle); }
      else if (h.act.ct <= 0 && h.act.t > 0.3) { h.act.ct += h.act.cycle; h.c.danceT = h.act.t0; }   // next loop
    }
    else if ((h.idleT -= dt) <= 0) { h.idleT = 9 + Math.random() * 7; h.idle = h.idle === 'menu_idle' ? 'locker_idle' : 'menu_idle'; h.c.setDance(h.idle); }
  }

  // A still backdrop: run the bots for a few seconds so the stage has some ink on it, then stop simulating and hide them.
  // (Only the camera keeps moving. Settings → Video → "Live menu background" brings the running match back.)
  _attractShot() {
    // For Halyard Marina, choose dramatic harbor angles (the backdrop has nobody on it: orbits only)
    const shots = ['harbor', 'orbit', 'orbit2'];
    const s = shots[this.shotIdx++ % shots.length];
    if (s === 'harbor') {
      this.shotT = 11;
      this.rig.orbit(new THREE.Vector3(0, 3.5, 0), 28, 11, 0.04, 1.2);
      return;
    }
    this.shotT = 10;
    if (s === 'orbit') this.rig.orbit(new THREE.Vector3(0, 1, 0), 34, 17, 0.05, Math.random() * 6);
    else this.rig.orbit(new THREE.Vector3(0, 2, -8), 18, 7, -0.07, Math.random() * 6);
  }
  _updateAttract(dt) {
    if (this._menuSceneOn) return;   // the main menu holds its own scenic shot
    this.attractT += dt; this.shotT -= dt;
    if (this.menus?.current === 'title') { if (this.rig.mode !== 'orbit') this.rig.orbit(new THREE.Vector3(0, 1, 0), 34, 17, 0.05, 0); }
    else if (this.shotT <= 0) this._attractShot();
  }

  // ---------------------------------------------------------------------------------------- match flow
  // lamps, signs, lit windows: follow the environment's night factor (0 day / golden … 1 dusk)
  _applyNight() {
    const k = G.env?.getSkyColors?.()?.night ?? 0;
    this.props?.setNight?.(k); this.decor?.setNight?.(k);
    // dusk: lanterns bright enough to bloom, and they pool warm light on the deck around them
    if (this.decor?.bulbMat) this.decor.bulbMat.emissiveIntensity *= 1 + 3.2 * k;
    for (const m of [this.levelMat, this.grateMat]) setLevelLamps(m, G.level, k);
  }

  async startMatch(o = {}) {
    const practice = !!o.practice;
    const opts = {
      mapId: o.mapId === 'sunset' ? 'tidewater' : (o.mapId || this.mapDef.id),
      time: o.mapId === 'sunset' ? 'dusk' : (o.time || this.time || 'day'),
      difficulty: o.difficulty || this.settings.difficulty,
      // Zone Control / Boss Battle from the stage select's MODE; practice is always turf
      mode: o.practice ? 'turf' : o.mode === 'zones' ? 'zones' : o.mode === 'boss' ? 'boss' : o.mode === 'duel' ? 'duel' : o.mode === 'deathmatch' ? 'deathmatch' : 'turf',
      duelRule: o.duelRule === 'deathmatch' ? 'deathmatch' : 'first5',
    };
    opts.duration = opts.mode === 'zones' ? (o.duration || ZONES.duration) : opts.mode === 'boss' ? (o.duration || BOSS_MODE.duration)
      : opts.mode === 'deathmatch' || (opts.mode === 'duel' && opts.duelRule === 'deathmatch') ? (o.duration || 180)
      : Math.min(MATCH.maxDuration, o.duration || MATCH.defaultDuration);
    if (!practice) this.lastMatchOpts = opts;
    G.audio?.init?.();
    G.audio?.unduck?.();   // a new stage picked from the practice pause menu starts un-ducked
    this.input.requestLock();
    this.menus?.show(null);
    await this._fade(1, 350);
    G.music?.stop?.(0.05); this._musicTrack = null;
    // start buffering this round's match song and the final-minute song while the world loads
    G.music?.preload?.('battle'); if (!practice) G.music?.preload?.('battle_final');
    this.showcase.hide();
    if (this.match) this.match.dispose();
    G.projectiles.clear(); G.subs.clear(); G.specials.clear(); G.fx.clear?.(); G.paint.clear(); this._clearDeathMarks();
    let map = MAPS.find((m) => m.id === opts.mapId) || MAPS[0];
    // offline never plays an online-only stage (the menus don't offer one; a stray ?autostart / api call falls back)
    if (!mapOfflineOk(map.id) && !DEV_STAGE) {
      console.warn(`[slimeduel] ${map.name} is online only — offline match on ${OFFLINE_MAPS[0].name} instead`);
      this.menus?.toast?.(`${map.name} is online only`);
      map = OFFLINE_MAPS[0];
    }
    if (opts.mode === 'boss' && !mapBossOk(map.id)) opts.mode = 'turf';
    await this._buildWorld(map, opts.mode);   // no-op when this stage (+ mode variant) is already built
    const theme = mapTheme(map, opts.time);
    this.time = opts.time === 'dusk' ? 'dusk' : 'day';
    if (theme !== this.theme) {
      this.theme = theme;
      G.env.setTheme?.(theme);
      if (G.env.envMap) G.scene.environment = G.env.envMap;
      G.fx.setLighting?.(G.env.getSkyColors?.());
    }
    this._applyNight();   // after any stage rebuild too (new prop kit / decor)
    this.mapDef = map;
    const savedProf = loadJSON('slimeduel.profile', DEFAULT_PROFILE);
    this.profile = { ...this.profile, ...savedProf };
    const localMorph = String(opts.morph || this.profile.morph || savedProf.morph || 'squid').replace(/^morph_/, '').toLowerCase();
    this.profile.morph = localMorph;
    this._setPalette(this._pickPalette());
    const m = (this.match = G.match = new Match({
      attract: false, practice, duration: opts.duration, mode: opts.mode, difficulty: opts.difficulty, weapon: this.profile.weapon || 'shooter', sub: this._subFor(this.profile.weapon), special: this._specialFor(this.profile.weapon),
      playerName: this.profile.name || 'Player', CharacterClass: this.CharacterClass, rig: this.rig, input: this.input,
      autopilot: params.has('autopilot'), style: this.profile.style || null, noBots: mapNoBots(map.id),   // (devstage: a solo walk)
      morph: localMorph, duelRule: opts.duelRule,
    }));
    m.setup();
    await this._warmCharacters(m);
    this.minimap.setViewerTeam(0);
    G.mode = 'match';
    this.hud?.setVisible(false);
    this.hudPrompt = null; this._hintT = 0; this._hints = {};
    this.hud?.setPractice?.(practice);
    m.start();
    if (practice || params.has('skipIntro') || params.has('autostart')) {
      // no intro fly-over: straight in
      m.setState('playing');
      if (m.local && practice) m.local.special = m.local.specialCost();
      this.hud?.setVisible(true);
    }
    this._fade(0, 500);
  }

  _inPractice() { return !!(this.match && this.match.practice && G.mode === 'match'); }

  // a random stage (a different one from the current, when there's a choice)
  startPractice(o = {}) {
    const pool = OFFLINE_MAPS.filter((m) => m.id !== this.mapDef?.id);   // (never an online-only stage)
    const map = (o.mapId && OFFLINE_MAPS.find((m) => m.id === o.mapId)) || pool[(Math.random() * pool.length) | 0] || OFFLINE_MAPS[0];
    return this.startMatch({ mapId: map.id, practice: true });
  }

  // practice: open the loadout screen straight from play (Esc on it drops you back in)
  openPracticeLoadout() {
    const m = this.match;
    if (!this._inPractice() || m.paused || m.state !== 'playing') return;
    m.paused = true;
    this.input.exitLock();
    this.menus?.show('loadout', { under: ['pause'], quick: true });
    G.audio?.duck?.(0.5, 99);
    G.music?.pause?.();
  }

  // equip the profile's loadout on the practising player (called whenever the loadout changes)
  _applyPracticeLoadout() {
    if (!this._inPractice()) return;
    const a = this.match.local;
    if (!a) return;
    const w = this.profile.weapon || 'shooter', sub = this._subFor(w), sp = this._specialFor(w);
    if (a.weaponId !== w || a.specialId !== sp) {
      G.specials.end(a, 'swap');
      a.specialActive = null;
      if (a.weaponId !== w) a.setWeapon(w);
      a.setSpecial(sp);
      a.special = a.specialCost();
    }
    if (a.subId !== sub) a.setSub(sub);
    a.ink = PLAYER.inkMax;
    let cur = {};
    try { cur = JSON.parse(localStorage.getItem('slimeduel.profile') || '{}'); } catch {}
    const morph = String(cur.morph || this.profile.morph || 'squid').replace(/^morph_/, '').toLowerCase();
    this.profile.morph = morph;
    if (a.setMorph) a.setMorph(morph);
    a.morph = morph;
    if (a.character?.setMorph) a.character.setMorph(morph);
  }

  // practice: wipe the stage clean and drop back in at spawn with full ink and special
  practiceReset() {
    if (!this._inPractice()) return;
    const a = this.match.local;
    G.projectiles.clear(); G.subs.clear(); G.specials.clear(); G.fx.clear?.(); G.paint.clear(); this._clearDeathMarks();
    if (!a) return;
    a.respawn();
    a.special = a.specialCost();
    a.stats.turf = 0; a.stats.splats = 0; a.stats.deaths = 0; a.stats.specials = 0;
  }

  // ---- online (src/net/session.js drives these) -----------------------------------------------------------------
  // Build the host's match: same stage / time / palette / roster on every client; the intro starts on netMatchGo.
  async startNetMatch(cfg, nm) {
    G.audio?.init?.();
    this.menus?.show(null);
    await this._fade(1, 350);
    G.music?.stop?.(0.05); this._musicTrack = null;
    this.showcase.hide();
    if (this.match) this.match.dispose();
    G.projectiles.clear(); G.subs.clear(); G.specials.clear(); G.fx.clear?.(); G.paint.clear(); this._clearDeathMarks();
    const map = MAPS.find((m) => m.id === cfg.map) || MAPS[0];
    await this._buildWorld(map, cfg.mode);   // no-op when this stage (+ mode variant) is already built
    const theme = mapTheme(map, cfg.time);
    this.time = cfg.time === 'dusk' ? 'dusk' : 'day';
    if (theme !== this.theme) {
      this.theme = theme;
      G.env.setTheme?.(theme);
      if (G.env.envMap) G.scene.environment = G.env.envMap;
      G.fx.setLighting?.(G.env.getSkyColors?.());
    }
    this._applyNight();
    this.mapDef = map;
    this._setPalette(this.settings.colorblind ? COLORBLIND_PALETTE : TEAM_PALETTES[cfg.palette] || TEAM_PALETTES[0]);
    const m = (this.match = G.match = new Match({
      attract: false, duration: cfg.duration, difficulty: cfg.difficulty, mode: cfg.mode, CharacterClass: this.CharacterClass, rig: this.rig, input: this.input,
      roster: cfg.roster, myId: G.net.myId, host: G.net.isHost, autopilot: params.has('autopilot'), duelRule: cfg.duelRule,
    }));
    m.setup();
    await this._warmCharacters(m);   // before 'ready': nobody starts until every shader a slime dualist can use is compiled
    nm.bind(m);
    this.lastMatchOpts = null;
    this.minimap.setViewerTeam(m.local ? m.local.team : 0);
    G.mode = 'match';
    this.hud?.setVisible(false);
    this.hudPrompt = null; this._hintT = 0; this._hints = {};
    // hold on the stage (not black) while the others finish loading
    if (m.local) { this.rig.follow(m.local, true); this.rig.yaw = m.local.team === 0 ? 0 : Math.PI; }
    this.rig.dioFlip = !!(m.local && m.local.team === 1);
    this._fade(0, 450);
  }
  // Compile every shader variant a slime dualist can use this match (all detail tiers, their cross-fades, squid form,
  // weapons) and build every kid's tier meshes while the screen is still faded out, so none of it lands mid-match.
  // Programs are shared, so after the first kid of each weapon the rest are a few ms each.
  async _warmCharacters(m) {
    const jobs = m.actors.map((a) => a.character?.warmAll ? Promise.resolve(a.character.warmAll()).catch((e) => console.warn('[slimeduel] warm', e)) : null).filter(Boolean);
    if (m.boss) jobs.push(Promise.resolve(m.boss.model.ready).then(() => Promise.all(m.boss.warm().map((o) => G.renderer.compileAsync(o, G.camera, G.scene)))).catch((e) => console.warn('[slimeduel] boss warm', e)));
    if (jobs.length) await Promise.race([Promise.all(jobs), new Promise((r) => setTimeout(r, 8000))]);   // never hang a load
  }
  netMatchGo() {
    const m = this.match;
    if (!m || m.state !== 'init') return;
    this.input.requestLock();
    m.start();
  }
  // results done → everyone back in the room's lobby
  async netMatchEnd() {
    if (this._netEnding) return;
    this._netEnding = true;
    clearTimeout(this._netEndT);
    try {
      this.input.exitLock();
      this.menus?.show(null);
      await this._fade(1, 350);
      this.hud?.setVisible(false);
      this.hud?.hideSplatted?.();
      this.showcase.hide();
      G.mode = 'menu';
      G.net?.endMatch();
      await this._menuStage();
      this._startAttract();
      this.menus?.show(this.menus?.hasScreen?.('lobby') === false ? 'main' : 'lobby');
      this._playMusic('menu');
      G.audio?.unduck?.();
      this._fade(0, 500);
    } finally { this._netEnding = false; }
  }
  // the room went away mid-match (connection lost): back to the menus with the reason
  netMatchAborted(reason) {
    // `reason` is an error code (src/net/errors.js) or a legacy message — menus turns it into a translatable line
    this.quitToMenu().then(() => { if (reason) this.menus?.toast?.(this.menus?.netErrorText?.(reason) || 'Lost connection to the room'); });
  }

  _intro() {
    if (this.match.boss) { this._bossIntro(this.match.boss); return; }
    const L = G.level;
    const local = this.match.local;
    const team = local ? local.team : 0, pad = L.spawnPads[team];
    const sgn = team === 0 ? 1 : -1;     // the layout is a 180° mirror: Bravo's shot is Alpha's rotated about the centre
    // sweep from high over the enemy base down behind the player (a stage can open on its own hero shot instead)
    const I = L.layout?.intro;
    const from = I ? new THREE.Vector3(I.from[0] * sgn, I.from[1], I.from[2] * sgn) : new THREE.Vector3(18 * sgn, 26, 30 * sgn);
    const to = new THREE.Vector3(pad.x, pad.y + 2.6, pad.z - (I?.toBack ?? 5.2) * sgn);
    const lookFrom = I ? new THREE.Vector3(I.lookFrom[0] * sgn, I.lookFrom[1], I.lookFrom[2] * sgn) : new THREE.Vector3(0, 0, 10 * sgn), lookTo = new THREE.Vector3(pad.x, pad.y + 1.6, pad.z + 6 * sgn);
    this.rig.cinematic(from, to, lookFrom, lookTo, 3.6, () => {});
    this.rig.yaw = team === 0 ? 0 : Math.PI; this.rig.pitch = -0.12;
    this.rig.dioFlip = team === 1;
    G.audio?.play('ready');
    setTimeout(() => { if (this.match?.state === 'intro') this.hud?.banner('ready'); }, 1700);
    setTimeout(() => { if (this.match?.state === 'intro') this.hud?.setVisible(true); }, 3000);
    this._playMusic(null);
  }

  pause() {
    if (!this.match || this.match.attract || this.match.paused) return;
    // only a live round (or its intro) can pause — never on top of time's up / judge / results
    if (this.match.state !== 'playing' && this.match.state !== 'intro') return;
    if (G.netm) {   // online: the match carries on underneath the menu
      this.input.exitLock();
      if (this.match.controller) this.match.controller.enabled = false;
      this.menus?.show('pause');
      return;
    }
    this.match.paused = true;
    this.announcer?.stop?.();
    this.input.exitLock();
    this.menus?.show('pause');
    G.audio?.duck?.(0.5, 99);
    G.music?.pause?.(); // a recording holds its place so the final-minute song stays in step with the clock
  }
  resume() {
    if (!this.match) return;
    this.menus?.show(null);
    this.match.paused = false;
    this._relock = true;
    if (this.match.controller) this.match.controller.enabled = true;
    this.input.requestLock();
    G.audio?.unduck?.();
    G.music?.resume?.();
  }
  async quitToMenu(screen = 'main') {
    clearTimeout(this._netEndT);
    this.announcer?.stop?.();
    if (G.net && G.net.state !== 'offline' && G.net.state !== 'error') G.net.leave();
    this.input.exitLock();
    this.menus?.show(null);
    await this._fade(1, 350);
    this.hud?.setVisible(false);
    this.hud?.hideSplatted?.();
    this.showcase.hide();
    G.mode = 'menu';
    this._setPalette(this._pickPalette());
    await this._menuStage();
    this._startAttract();
    this.hud?.setPractice?.(false);
    this.menus?.show(screen);
    this._playMusic('menu');
    G.audio?.unduck?.();
    this._fade(0, 500);
  }

  // Boss Battle intro: sweep in over the arena as HULLBREAKER bursts out of its home ground (boss time 1.8 s), hold on the
  // roar while the title card plays, then swing back down behind the squad's pad for GO. Chained on the rig's own
  // clock (not timers), so it stays in step with the boss under freeze/step audits.
  _bossIntro(b) {
    const pad = G.level.spawnPads[0], B = b.pos, fy = b.yaw;
    const fx = Math.sin(fy), fz = Math.cos(fy), sx = fz, sz = -fx;       // its forward (toward the squad) and a side
    const reveal = new THREE.Vector3(B.x + fx * 15 + sx * 5, B.y + 4.6, B.z + fz * 15 + sz * 5);
    const push = new THREE.Vector3(B.x + fx * 12 + sx * 3.2, B.y + 3.4, B.z + fz * 12 + sz * 3.2);
    const behind = new THREE.Vector3(pad.x, pad.y + 2.6, pad.z - 5.2);
    const chest = new THREE.Vector3(B.x, B.y + 2.6, B.z), head = new THREE.Vector3(B.x, B.y + 3.4, B.z);
    const high = new THREE.Vector3(pad.x + 6, pad.y + 17, pad.z + 4), mid = new THREE.Vector3((pad.x + B.x) / 2, 0, (pad.z + B.z) / 2);
    const m = this.match;
    this.rig.cinematic(high, reveal, mid, chest, 1.9, () => {
      if (this.match !== m || m.state !== 'intro') return;
      this.rig.cinematic(reveal, push, chest, head, 3.0, () => {
        if (this.match !== m || m.state !== 'intro') return;
        this.rig.cinematic(push, behind, head, new THREE.Vector3(pad.x, pad.y + 1.6, pad.z + 6), 2.1, () => {});
      });
    });
    this.rig.yaw = 0; this.rig.pitch = -0.12; this.rig.dioFlip = false;
    G.audio?.play('ready');
    setTimeout(() => { if (this.match === m && m.state === 'intro') this.hud?.banner('ready'); }, 5300);
    setTimeout(() => { if (this.match === m && m.state === 'intro') this.hud?.setVisible(true); }, 5600);
    this._playMusic(null);
  }
  // round over: circle the boss (sinking, or roaring over a squad that ran out of time)
  _bossFinishCam(b) {
    const c = new THREE.Vector3(b.pos.x, b.pos.y + 2.2, b.pos.z), cam = G.camera.position;
    this.rig.orbit(c, 15, 5.5, 0.09, Math.atan2(cam.x - c.x, cam.z - c.z));
  }
  // daily challenges: feed the finished match in and announce any that just completed
  // ELO (game/elo.js): ranks you and orders the leaderboard. Online ×2 · ranked ×10; PRACTICE (vs bots) gives none.
  _eloAfterMatch(m, local, won, p) {
    try {
      if (!G.netm || !local || !local.stats) return null;
      const s = local.stats, mode = m.mode || (m.result && m.result.mode);
      const mine = (a) => (mode === 'boss' ? a.stats.bossDmg || 0 : mode === 'deathmatch' || mode === 'duel' ? a.stats.splats || 0 : a.stats.turf || 0);
      const mates = (m.actors || []).filter((a) => a.stats && (mode === 'deathmatch' || mode === 'duel' || mode === 'boss' || a.team === local.team));
      const mvp = mates.length > 1 && mates.every((a) => a === local || mine(a) <= mine(local));
      const draw = !won && !!m.result && (m.result.winner === -1 || m.result.tie === true);
      const kind = Elo.matchKind({ online: !!G.netm, ranked: !!(G.netm && G.net && G.net.lobby && G.net.lobby.ranked) });
      return Elo.applyMatch(p, { won: !!won, draw, splats: s.splats || 0, deaths: s.deaths || 0, turf: Math.round(s.turf || 0), mvp, kind });
    } catch (e) { console.warn('[elo]', e); return null; }
  }
  // Death screen SPECTATE ([Space] / A): keep watching whoever splatted you — then whoever's next when they go down, or
  // ←/→ (LB/RB) to switch — and stay out until you press it again to respawn (actor.holdRespawn).
  _spectateTick(m) {
    const loc = m.local, inp = this.input, rig = this.rig;
    if (!loc || loc.alive || m.state !== 'playing' || m.paused) {
      if (this._spectating) { this._spectating = false; if (loc) loc.holdRespawn = false; if (rig.spectate) rig.spectate.pov = false; this.hud?.hideSpectate?.(); }
      this._specPov = false; if (this.hud) this.hud.viewAs = null;
      return;
    }
    const c = m.controller;
    const press = !(c && (c.mapHeld || c.scoreboardHeld)) && (inp.wasPressed('Space') || inp.padPressed.has(0));
    if (!this._spectating) {
      if (!press || rig.mode !== 'spectate' || !rig.spectate) return;
      this._spectating = true; loc.holdRespawn = true;
      rig.spectate.pov = true; rig._startBlend(0.5);
      this.hud?.hideSplatted?.();
      this.screenfx?.clearFlood?.();
    } else if (press) loc.holdRespawn = false;   // respawn now, or the moment the timer runs out
    const s = rig.spectate;
    if (!s) return;
    const pool = m.actors.filter((a) => a !== loc && a.alive);
    const step = inp.wasPressed('ArrowRight') || inp.padPressed.has(5) ? 1 : inp.wasPressed('ArrowLeft') || inp.padPressed.has(4) ? -1 : 0;
    let tgt = s.actor && s.actor.alive ? s.actor : null;
    if (pool.length && (step || !tgt)) {
      if (step) tgt = tgt ? pool[(pool.indexOf(tgt) + step + pool.length) % pool.length] : pool[0];
      else { const at = rig.lookAt; tgt = pool.reduce((b, a) => (a.pos.distanceToSquared(at) < b.pos.distanceToSquared(at) ? a : b)); }
      if (s.actor !== tgt) { s.actor = tgt; rig._startBlend(0.45); }
    }
    // [P] / Y: player view — the spectate bar goes and the HUD becomes the watched player's (their ink, weapon, special…)
    if (inp.padPressed.has(3)) this._specPov = !this._specPov;
    const pov = !!(this._specPov && tgt);
    if (this.hud) this.hud.viewAs = pov ? tgt : null;
    // [R] / the bar's button: report the player you're watching (pauses into the support form, their name filled in)
    if (this.hud) this.hud.onSpecReport = () => this._reportPlayer(tgt);
    if (tgt && inp.wasPressed('KeyR')) this._reportPlayer(tgt);
    if (pov) { this.hud?.hideSpectate?.(); return; }
    this.hud?.setSpectate?.({ name: tgt ? tgt.name : null, color: tgt ? (tgt.colorHex || G.teamHex[tgt.team]) : null,
      respawnIn: Math.max(0, loc.respawnTimer), queued: !loc.holdRespawn });
  }
  /** 2 while a Double XP token runs (shop-extras.js; the shop saves it straight to the stored profile), else 1. */
  _xpBoost(p) {
    let until = +(p && p.xpBoostUntil) || 0;
    try { until = Math.max(until, +JSON.parse(localStorage.getItem('slimeduel.profile') || '{}').xpBoostUntil || 0); } catch (e) { /* private mode */ }
    if (p && until > (+p.xpBoostUntil || 0)) p.xpBoostUntil = until;
    return until > Date.now() ? 2 : 1;
  }
  _reportPlayer(a) {
    if (!a || !this.menus) return;
    const name = a.name;
    this.pause();
    setTimeout(() => { if (this.menus.current === 'pause') this.menus._openSupport({ category: 'report', player: name }); }, 450);
  }
  // career stats per main weapon (loadout screen): matches, wins, splats, deaths, turf
  _weaponCareer(p, local, won, turf) {
    const id = local?.weaponId;
    if (!id) return;
    const all = p.weaponStats || (p.weaponStats = {});
    const s = all[id] || (all[id] = { m: 0, w: 0, k: 0, d: 0, t: 0 });
    s.m++; if (won) s.w++;
    s.k += local.stats?.splats || 0; s.d += local.stats?.deaths || 0; s.t += Math.max(0, Math.round(turf || 0));
  }
  _dailyAfterMatch(local, mode, won) {
    try {
      const done = Daily.recordMatch(this.profile, Daily.matchFromActor(local, mode, won));
      done.forEach((d, i) => setTimeout(() => this.menus?.toast?.(`${d.set === 'weekly' ? 'WEEKLY' : d.set === 'season' ? 'SEASON' : 'DAILY'} CHALLENGE COMPLETE: ${d.def.text}`, { kind: 'good', ms: 4200 }), 1500 + i * 600));
    } catch (e) { console.warn('[daily]', e); }
  }
  // Boss Battle results: no turf judge — straight to VICTORY / DEFEAT with the fight's numbers
  async _bossResults() {
    const m = this.match, R = m.result, bo = R.boss || {};
    this.hud?.hideSplatted?.();
    await new Promise((r) => setTimeout(r, 400));
    if (this.match !== m) return;
    const won = !!bo.win, cov = R.coverage || G.paint.coverage();
    m.setState('results');
    this.hud?.setVisible(false);
    this._syncProfile();
    const local = m.local, p = this.profile;
    const turf = Math.round(local.stats.turf), dmg = Math.round(local.stats.bossDmg || 0);
    const gained = Math.round((won ? PROGRESSION.xpWin : PROGRESSION.xpLose) + turf * PROGRESSION.xpPerTurfPoint + local.stats.splats * PROGRESSION.xpPerSplat + dmg * 0.04) * this._xpBoost(p);
    const before = { level: p.level, xp: p.xp, toNext: PROGRESSION.xpForLevel(p.level) };
    p.xp += gained; p.matches++; if (won) p.wins++; p.totalTurf += turf;
    p.kills = (p.kills || 0) + (local.stats.splats || 0);
    p.deaths = (p.deaths || 0) + (local.stats.deaths || 0);
    this._weaponCareer(p, local, won, turf);
    let leveledUpCount = 0;
    const crateMilestones = [];
    while (p.xp >= PROGRESSION.xpForLevel(p.level)) {
      p.xp -= PROGRESSION.xpForLevel(p.level);
      p.level++;
      leveledUpCount++;
      if (p.level % 5 === 0) {
        p.lootBoxes = (p.lootBoxes || 0) + 1;
        crateMilestones.push(p.level);
      }
    }
    // Rebalanced Gems for match completion: rewarding progression without hyper-inflation
    const winBonus = won ? 75 : 30;
    const turfBonus = Math.round(turf * (won ? 0.03 : 0.02));
    const levelBonus = leveledUpCount * 50;
    const earnedGems = winBonus + turfBonus + levelBonus;
    const gemsBefore = p.coins || 0;
    p.coins = gemsBefore + earnedGems;
    const elo = this._eloAfterMatch(m, local, won, p);
    saveJSON('slimeduel.profile', p);
    this._dailyAfterMatch(local, 'boss', won);
    const data = {
      mode: 'boss', win: won, elo, percents: [cov[0] * 100, cov[1] * 100], colors: [G.teamHex[0], G.teamHex[1]], teamNames: this.palette.names || TEAM_NAMES,
      boss: { name: bo.name, defeated: won, time: bo.time, hpLeft: bo.maxHp ? bo.hp / bo.maxHp : 0, phase: bo.phase, maxHp: bo.maxHp },
      players: m.actors.map((a) => ({ name: a.name, team: 0, weapon: a.weaponId, turf: Math.round(a.stats.turf), damage: Math.round(a.stats.bossDmg || 0), weakHits: a.stats.weakHits || 0, splats: a.stats.splats, deaths: a.stats.deaths, isSelf: a.isLocal, bot: !!a.isBot })),
      xp: { gained, levelBefore: before.level, levelAfter: p.level, xpBefore: before.xp, xpAfter: p.xp, xpToNextBefore: before.toNext, xpToNextAfter: PROGRESSION.xpForLevel(p.level) },
      gems: { earned: earnedGems, total: p.coins, before: gemsBefore, parts: [[won ? 'VICTORY BONUS' : 'MATCH BONUS', winBonus], ['TURF SLIMED', turfBonus], ...(levelBonus > 0 ? [['LEVEL UP', levelBonus]] : [])] },
      crateMilestones: crateMilestones.length ? crateMilestones : undefined,
      mapName: this.mapDef.name,
    };
    // the squad's top four (by damage) on the podium
    const top = [...m.actors].sort((a1, a2) => (a2.stats.bossDmg || 0) - (a1.stats.bossDmg || 0)).slice(0, 4);
    this.showcase.showResults(0, won, G.teamColors[0], top.map((a) => ({ weapon: a.weaponId, style: a.character.style || { hair: a.slot % 4, skin: (a.slot * 3) % 4 }, name: a.name, skin: a.cos?.skin })));
    if (G.netm) data.online = true;
    this.menus?.showResults(data);
    this.menus?.show('results');
    G.audio?.play(won ? 'victory_fanfare' : 'defeat_jingle');
    if (G.netm && G.net.isHost) this._netEndT = setTimeout(() => { G.netm?.sendEnd(); this.netMatchEnd(); }, 12000);
  }

  async _judge() {
    try {
      const m = this.match;
      if (!m || !m.result) return;
      if (m.result?.mode === 'boss') return this._bossResults();
      this.hud?.hideSplatted?.();
      this.rig.overview();
      this.hud?.setVisible(true);
      const cov = (m.result && m.result.coverage) ? m.result.coverage : [0.5, 0.5];
      // Zone Control: the final counts (the scores) and each team's leftover penalty (shown apart, like the HUD's "+N"), winner and how it was won
      let zr = null;
      if (m.mode === 'zones' && m.zones && m.result.mode === 'zones') {
        const zs = m.zones.state();
        zr = { counts: [...zs.count], penalty: zs.penalty.map((p) => Math.max(0, Math.ceil(p - 1e-6))), winner: m.result.winner, reason: m.result.reason || 'time', overtime: !!m.result.overtime, overtimeT: zs.overtimeT };
      }
      const names = (this.palette && this.palette.names) || TEAM_NAMES;
      const isDeathmatch = m.mode === 'deathmatch';
      const isDuel = m.mode === 'duel';
      const act0 = m.actors.find((a) => a.team === 0) || m.actors[0];
      const act1 = m.actors.find((a) => a.team === 1) || m.actors[1];
      const judgeP = zr
        ? this.hud?.judge({ mode: 'zones', colors: [G.teamHex[0], G.teamHex[1]], names, counts: zr.counts, penalty: zr.penalty, winner: zr.winner, reason: zr.reason, overtime: zr.overtime, percents: [cov[0] * 100, cov[1] * 100] })
        : isDuel
        ? this.hud?.judge({
            mode: 'duel',
            winner: m.result?.winnerName || (m.result?.winner === 1 ? act1?.name : act0?.name) || 'Champion',
            winnerColor: G.teamHex?.[m.result?.winner] || (m.result?.winner === 1 ? G.teamHex?.[1] : G.teamHex?.[0]) || '#a6f01a',
            score: [act0?.stats?.splats || 0, act1?.stats?.splats || 0],
            knockout: !!m.result?.knockout,
            names: [act0?.name || 'You', act1?.name || 'Rival']
          })
        : isDeathmatch
        ? this.hud?.judge({ mode: 'deathmatch', winner: m.result?.winnerName || 'Champion', winnerColor: G.teamHex?.[m.result?.winner] || '#a6f01a' })
        : this.hud?.judge({ colors: [G.teamHex[0], G.teamHex[1]], percents: [cov[0] * 100, cov[1] * 100], names, winner: m.result.winner });
      await Promise.race([judgeP, new Promise((r) => setTimeout(r, 5200))]);
      const myRank = (isDeathmatch || isDuel) && m.result?.rankings ? (m.result.rankings.findIndex((r) => r.isLocal) + 1 || 1) : 1;
      const myTeam = m.local ? m.local.team : 0;
      const won = (isDeathmatch || isDuel) ? (myRank === 1) : (m.result.winner === myTeam);
      m.setState('results');
      this.hud?.setVisible(false);
      // profile / XP
      const local = m.local || m.actors[0] || { stats: { turf: 0, splats: 0, deaths: 0 } };
      this._syncProfile();
      const p = this.profile;
      const turf = Math.round((local?.stats?.turf) || 0);
      const splats = (local?.stats?.splats) || 0;
      let gained = Math.round((won ? PROGRESSION.xpWin : PROGRESSION.xpLose) + turf * PROGRESSION.xpPerTurfPoint + splats * PROGRESSION.xpPerSplat);
      let xpParts = null;
      if (isDuel) {
        const placeBonus = won ? 250 : 100;
        const splatBonus = splats * 35;
        const koBonus = m.result?.knockout && won ? 150 : 0;
        const turfBonus = Math.round(turf * 0.4);
        xpParts = [
          [won ? (m.result?.knockout ? 'KNOCKOUT!' : 'VICTORY') : 'MATCH FINISH', placeBonus],
          ['SPLATS', splatBonus],
          ...(koBonus > 0 ? [['KNOCKOUT BONUS', koBonus]] : []),
          ['TURF', turfBonus],
        ];
        gained = placeBonus + splatBonus + koBonus + turfBonus;
      } else if (isDeathmatch) {
        const placeBonus = myRank === 1 ? 250 : myRank === 2 ? 180 : myRank === 3 ? 140 : 80;
        const splatBonus = splats * 35;
        const turfBonus = Math.round(turf * 0.4);
        xpParts = [
          [myRank === 1 ? '1ST PLACE' : `#${myRank} PLACE`, placeBonus],
          ['SPLATS', splatBonus],
          ['TURF', turfBonus],
        ];
        gained = placeBonus + splatBonus + turfBonus;
      } else if (zr) {
        // Zone Control: less per point of turf (a 5 min match), extra for ink laid on the live zone, a knockout bonus
        const ZX = PROGRESSION.zones || { turfScale: 0.6, xpPerZoneTurfPoint: 1, xpKnockout: 300 };
        const zoneTurf = Math.round(local?.stats?.zoneTurf || 0);
        xpParts = [[won ? 'WIN BONUS' : 'MATCH', won ? PROGRESSION.xpWin : PROGRESSION.xpLose], ['TURF', Math.round(turf * PROGRESSION.xpPerTurfPoint * ZX.turfScale)],
          ['ZONE SLIME', Math.round(zoneTurf * ZX.xpPerZoneTurfPoint)], ['SPLATS', Math.round(splats * PROGRESSION.xpPerSplat)], ['KNOCKOUT', won && zr.reason === 'knockout' ? ZX.xpKnockout : 0]].filter(([, v], i) => i < 2 || v > 0);
        gained = xpParts.reduce((a, [, v]) => a + v, 0);
      }
      // Double XP token running (shop-extras.js): every part counts twice
      if (this._xpBoost(p) > 1) { if (xpParts) xpParts.push(['DOUBLE XP', gained]); gained *= 2; }
      const before = { level: p.level, xp: p.xp, toNext: PROGRESSION.xpForLevel(p.level) };
      p.xp += gained; p.matches++; if (won) p.wins++; p.totalTurf += turf;
      p.kills = (p.kills || 0) + ((local?.stats?.splats) || 0);
      p.deaths = (p.deaths || 0) + ((local?.stats?.deaths) || 0);
      this._weaponCareer(p, local, won, turf);
      let leveledUpCount = 0;
      const crateMilestones = [];
      while (p.xp >= PROGRESSION.xpForLevel(p.level)) {
        p.xp -= PROGRESSION.xpForLevel(p.level);
        p.level++;
        leveledUpCount++;
        if (p.level % 5 === 0) {
          p.lootBoxes = (p.lootBoxes || 0) + 1;
          crateMilestones.push(p.level);
        }
      }
      // Rebalanced Gems for match completion: rewarding progression without hyper-inflation
      const winBonus = isDeathmatch
        ? (myRank === 1 ? 80 : myRank === 2 ? 55 : myRank === 3 ? 40 : 25)
        : isDuel
        ? (won ? 75 : 30)
        : (won ? 60 : 25);
      const splatGemBonus = (isDeathmatch || isDuel) ? (splats * 5) : 0;
      const turfBonus = Math.round(turf * 0.025);
      const levelBonus = leveledUpCount * 50;
      const earnedGems = winBonus + splatGemBonus + turfBonus + levelBonus;
      const gemsBefore = p.coins || 0;
      p.coins = gemsBefore + earnedGems;
      const elo = this._eloAfterMatch(m, local, won, p);
      saveJSON('slimeduel.profile', p);
      this._dailyAfterMatch(local, m.mode, won);
      const data = {
        mode: m.mode, elo,
        knockout: !!m.result?.knockout,
        win: won, percents: [cov[0] * 100, cov[1] * 100], colors: [G.teamHex[0], G.teamHex[1]], teamNames: names,
        players: m.actors.map((a) => ({ name: a.name, team: a.team, weapon: a.weaponId, turf: Math.round(a.stats.turf), splats: a.stats.splats, deaths: a.stats.deaths, isSelf: a.isLocal, bot: !!a.isBot, ffaColor: a.ffaColor, colorHex: a.colorHex, ...(zr ? { zoneTurf: Math.round(a.stats.zoneTurf || 0) } : {}) })),
        rankings: m.result?.rankings,
        myRank,
        xp: { gained, levelBefore: before.level, levelAfter: p.level, xpBefore: before.xp, xpAfter: p.xp, xpToNextBefore: before.toNext, xpToNextAfter: PROGRESSION.xpForLevel(p.level), ...(xpParts ? { parts: xpParts } : {}) },
        gems: { earned: earnedGems, total: p.coins, before: gemsBefore, parts: [[isDeathmatch ? (myRank === 1 ? 'VICTORY' : `#${myRank} FINISH`) : (won ? 'VICTORY BONUS' : 'MATCH BONUS'), winBonus], ...(splatGemBonus > 0 ? [['SPLAT BONUS', splatGemBonus]] : []), ['TURF SLIMED', turfBonus], ...(levelBonus > 0 ? [['LEVEL UP', levelBonus]] : [])] },
        crateMilestones: crateMilestones.length ? crateMilestones : undefined,
        mapName: this.mapDef?.name || 'Arena',
        ...(zr ? { mode: 'zones', zones: zr } : {}),
      };
      // your team on the podium
      const team = (isDeathmatch || isDuel)
        ? m.actors.filter((a) => a.name === (m.result?.winnerName || m.local?.name))
        : m.actors.filter((a) => a.team === myTeam);
      const myColRaw = ((isDeathmatch || isDuel) && team[0]?.ffaColor) || (G.teamColors && G.teamColors[myTeam]) || (myTeam === 1 ? '#2f5bff' : '#ff8a14');
      const myCol = myColRaw instanceof THREE.Color ? myColRaw : new THREE.Color(myColRaw);
      const podStyle = (a) => ({ weapon: a.weaponId, style: a.character?.style || { hair: a.slot % 4, skin: (a.slot * 3) % 4 }, name: a.name, skin: a.cos?.skin });
      if (isDuel && m.actors.length >= 2) {
        const duelists = [...m.actors].sort((a, b) => a.team - b.team).slice(0, 2);
        const wName = m.result?.winnerName;
        this.showcase.showResults(myTeam, true, myCol, duelists.map(podStyle), { duel: { winners: duelists.map((a) => a.name === wName), colors: duelists.map((a) => a.ffaColor || a.color || myCol) } });
      } else this.showcase.showResults(myTeam, won, myCol, team.map(podStyle));
      if (G.netm) data.online = true;   // the results screen builds its online variant (back-to-room countdown) from this
      this.menus?.showResults(data);
      this.menus?.show('results');
      G.audio?.play(won ? 'victory_fanfare' : 'defeat_jingle');
      setTimeout(() => this._playMusic(won ? 'results_win' : 'results_lose'), 2600);
      // online: the host brings the room back to the lobby once everyone has seen the results
      if (G.netm) {
        if (G.net?.isHost) this._netEndT = setTimeout(() => { G.netm?.sendEnd(); this.netMatchEnd(); }, 12000);
      }
    } catch (err) {
      console.error('[judge] error during match judging:', err && err.stack ? err.stack : err);
      this.hud?.setVisible(false);
      this.menus?.show('main', { wipe: true });
    }
  }

  _fade(to, ms) {
    return new Promise((r) => {
      const el = this.fadeEl;
      if (!el) return r();
      el.style.transition = `opacity ${ms}ms ease`;
      el.style.opacity = String(to);
      el.style.pointerEvents = to > 0.5 ? 'all' : 'none';
      setTimeout(r, ms + 20);
    });
  }

  // ---------------------------------------------------------------------------------------- loop
  // ts is the rAF (vsync) timestamp: stepping the sim by vsync-to-vsync intervals instead of callback times keeps motion
  // even on 120 Hz displays. settings.fpsCap (0 = display rate) skips vsyncs so frames land on a steady cadence.
  _loop(ts) {
    requestAnimationFrame((t) => this._loop(t));
    this._trackVsync(ts);
    const cap = this.settings.fpsCap || 0;
    if (cap && ts !== undefined && this._lastTs !== undefined && ts - this._lastTs < 1000 / cap - 2) return;
    this._lastTs = ts;
    this.timer.update(ts); let dt = Math.max(0, this.timer.getDelta());
    if (this.frozen || this._building) return;
    this.fpsAcc += dt; this.fpsN++;
    if (this.fpsAcc > 0.5) { this.fps = Math.round(this.fpsN / this.fpsAcc); this.fpsAcc = 0; this.fpsN = 0; }
    this._dynRes(dt);
    dt = Math.min(dt, 1 / 24);
    this._frame(dt);
  }

  // Display refresh estimate: 10th percentile of raw rAF intervals (rAF fires every vsync while frames keep up, and
  // keeps doing so under an fps cap because capped frames are skipped here, not in the browser).
  _trackVsync(ts) {
    if (ts === undefined) return;
    const v = this._vs || (this._vs = { last: ts, buf: [], ms: 1000 / 60 });
    const d = ts - v.last; v.last = ts;
    if (d > 2 && d < 50) v.buf.push(d);
    if (v.buf.length >= 90) {
      v.buf.sort((a, b) => a - b);
      // timestamp jitter makes the raw percentile read a little short (7.8 ms on a 120 Hz panel): snap to a real rate
      const hz = 1000 / v.buf[9];
      v.ms = 1000 / REFRESH_RATES.reduce((best, r) => (Math.abs(r - hz) < Math.abs(best - hz) ? r : best));
      v.buf.length = 0;
    }
  }
  // the frame interval (ms) we pace and size the render for: the display's refresh, or the user's cap if that is lower
  _frameTarget() {
    const cap = this.settings.fpsCap || 0;
    return Math.max(this._vs?.ms || 1000 / 60, cap ? 1000 / cap : 0);
  }

  // hold the frame target: when a 4 s window of a live round averages over ~112% of it (frames missing vsync, which
  // on a 120 Hz display reads as 8/16 ms judder), drop render density one notch. Stepping back up needs 12 s locked
  // at the target and happens at most twice, so the image never pumps between sizes (re-sizing every couple of
  // seconds read as flicker).
  _dynRes(dt) {
    if (dt <= 0 || dt > 0.25) return;
    const d = this._dyn || (this._dyn = { acc: 0, n: 0, t: 0, fast: 0, ups: 0 });
    d.acc += dt; d.n++; d.t += dt;
    if (d.t < 4) return;
    const avg = d.acc / d.n;
    d.acc = 0; d.n = 0; d.t = 0;
    const m = this.match;
    if (this.settings.quality === 'ultra' || document.hidden || !m || m.attract || m.state !== 'playing') { d.fast = 0; return; }
    const s = this.R.dynScale || 1, tgt = this._frameTarget() / 1000;
    if (avg > tgt * 1.12 && s > this.R.dynFloor() + 0.01) { this.R.setDynamicScale(s - 0.125); d.fast = 0; }
    else if (avg < tgt * 1.04 && s < 1 && d.ups < 2) { if (++d.fast >= 3) { this.R.setDynamicScale(s + 0.125); d.fast = 0; d.ups++; } }
    else d.fast = 0;
  }

  _frame(dt) {
    const tA = performance.now();
    G.renderer.info.reset();
    G.time += dt;
    this.input.pollPad();
    this._padMenus();
    G.net?.update?.(dt);
    const m = this.match;
    // the online lobby / hub stand in their own full-frame set (showcase): the attract match behind them is neither
    // simulated nor drawn while it's covered — it resumes exactly where it was when the set fades away
    const setUp = !!this.showcase?.fullFrame;
    if (m && !(setUp && m.attract) && !(m.attract && m.frozen)) {
      m.updateController(dt);
      const sub = dt > 1 / 45 ? 2 : 1; // substep physics on slow frames
      for (let i = 0; i < sub; i++) m.update(dt / sub);
      if (!m.paused) { G.projectiles.update(dt); G.subs.update(dt); G.specials.update(dt); }
      if (m.attract) this._updateAttract(dt);
      else if (m.state === 'playing' && m.local?.alive && this.rig.mode !== 'follow' && this.rig.mode !== 'path') this.rig.follow(m.local, true);
    } else if (m && m.attract && m.frozen && !setUp) this._updateAttract(dt);   // still backdrop: just the camera
    if (m && !m.attract) this._spectateTick(m);
    if (this._mouseFree && (document.pointerLockElement || this.menus?.current || !G.netm || !m || m.state !== 'playing')) { this._mouseFree = false; this.hud?.setMouseFree?.(false); }
    if (!m || !m.paused) G.fx.update(dt, G.camera);
    if (!m || !m.paused) this.fxHooks?.update?.(dt);
    if (m && !m.paused && !m.attract) this._ageDeathMarks(dt);
    this.screenfx?.update?.(dt, this);
    this._updateMenuHero(dt);
    // the TAB map (also while splatted, planning a Super Jump): lift the splatted desaturation + ink flood off the stage
    // so the ink and the pins read
    // (fully gone well before the map is fully open: the flood's drips draw at any alpha above zero)
    const fxU = this.screenfx?.U, mk = this.rig.mapK || 0;
    if (fxU && mk > 0 && fxU.uDesat && fxU.uFlood) {
      const e = Math.min(1, mk / 0.8), k = 1 - e * e * (3 - 2 * e);
      fxU.uDesat.value *= k; fxU.uFlood.value.w = k > 0.02 ? fxU.uFlood.value.w * k : 0;
    }
    G.env.update?.(dt, G.camera);
    this.decor.update(dt);
    this.props?.update?.(dt, G.time);
    // map diorama: held map key during live play (or while waiting to respawn) swoops the view overhead — but not while
    // aiming a Vortex Strike (that uses the flat stage map)
    const strikeAiming = !!(m?.local?.specialActive && m.local.specialActive.id === 'strike' && m.local.specialActive.aiming);
    this.rig.setMap?.(!!(m && !m.attract && !m.paused && m.state === 'playing' && m.controller?.mapHeld && !this.menus?.current && !strikeAiming));
    this.rig.update(dt);
    this._dioFog();
    this.diorama?.update(dt, this.rig.mapK);
    // local player camera-dependent aim must use this frame's camera
    if (m && m.controller && m.state === 'playing') m.controller.computeAim?.();
    // bomb arc preview
    const loc = m?.local;
    const orbReady = !!(loc && loc.specialActive && loc.specialActive.id === 'booyah' && loc.specialActive.charge >= 1);
    G.projectiles.updateArc(loc, !!(loc && loc.alive && (loc.weaponRunner.aimingSub || orbReady) && m.state === 'playing' && !m.paused));
    const tB = performance.now();
    // paint → atlas, shader uniforms
    G.paint.flush(dt);
    this.levelMat.userData.uniforms.uTime.value = G.time;
    // see-through window toward the local player
    {
      const lu = this.levelMat.userData.uniforms;
      const on = !!(m && !m.attract && loc && loc.alive && this.rig.mode === 'follow' && this.rig.target === loc && (this.rig.mapK ?? 0) < 0.3);
      lu.uSeeOn.value = damp(lu.uSeeOn.value, on ? 1 : 0, 10, dt);
      lu.uSeeA.value.copy(G.camera.position);
      if (loc) lu.uSeeB.value.set(loc.pos.x, loc.pos.y + (loc.form === 'squid' ? 0.4 : 1.0), loc.pos.z);
      if (this.grateMat) { const gu = this.grateMat.userData.uniforms; gu.uSeeOn.value = lu.uSeeOn.value; gu.uSeeA.value.copy(lu.uSeeA.value); gu.uSeeB.value.copy(lu.uSeeB.value); }
    }
    if (this.grateMat) this.grateMat.userData.uniforms.uTime.value = G.time;
    // swimmers' wakes in the ink surface
    if (this.swimWake && (!m || !m.paused)) this.swimWake.update(dt, this.levelMat.userData.uniforms, G.camera.position);
    // music self-heal: a live, unpaused round never sits in silence because a recording was left paused
    if (m && !m.paused && !m.attract && G.mode === 'match' && (this._musicHealT = (this._musicHealT || 0) + dt) > 1) {
      this._musicHealT = 0;
      const ct = G.music?.current;
      if (ct && ct.el && ct.el.paused && !ct.stopped && !ct.el.ended && (m.state === 'playing' || m.state === 'intro')) {
        if (ct.id === 'battle' || ct.id === 'battle_final') {
          G.music.resume();
        } else {
          try { ct.dispose(); } catch {}
          G.music.current = null;
        }
      }
    }
    this.showcase.update(dt);
    this._updateLocalLoops(dt);
    this._updateAmbience(dt);
    // audio listener
    if (G.audio?.setListener) {
      const cam = this.rig.gameCam || G.camera;   // the player's ears stay with the player while the map is up
      G.audio.setListener(cam.position, cam.getWorldDirection(this._lf || (this._lf = new THREE.Vector3())), cam.up);
    }
    // post uniforms (low-hp vignette)
    const g = this.R.grade.uniforms;
    const hpK = loc && m && !m.attract && loc.alive ? clamp(1 - loc.hp / 55, 0, 1) : 0;
    g.uHurt.value = damp(g.uHurt.value, hpK * 0.8, 6, dt);
    if (loc) g.uHurtColor.value.copy(G.teamColors[loc.enemyTeam]);
    // shadows: every frame (half-rate updates made moving shadows — your own, right under the crosshair — judder);
    // only the low preset halves it
    const sm = G.renderer.shadowMap;
    sm.autoUpdate = false;
    this._frameN = (this._frameN || 0) + 1;
    // the menu backdrop is a still stage with one slow-moving slime: its shadow map refreshes every 3rd frame
    const menuStill = !!(m && m.attract && m.frozen);
    if (menuStill ? (this._frameN % 3 === 0) : (this.settings.quality !== 'low' || (this._frameN & 1))) sm.needsUpdate = true;
    if (!this._skipRender) {
      if (!setUp) this.R.render();
      if (this.showcase.mode) sm.needsUpdate = true;
      this.showcase.render();
    }
    const tC = performance.now();
    const ps = this.perf || (this.perf = { sim: 0, render: 0, calls: 0, tris: 0 });
    ps.sim += (tB - tA - ps.sim) * 0.05; ps.render += (tC - tB - ps.render) * 0.05;
    ps.calls = G.renderer.info.render.calls; ps.tris = G.renderer.info.render.triangles;
    // HUD
    if (m && !m.attract && this.hud && (m.state === 'playing' || m.state === 'intro' || m.state === 'finish')) this._updateHud(dt);
    this.menus?.update?.(dt);
    this.input.endFrame();
  }

  // continuous sounds tied to the local player's state (swim gurgle, wall climb, enemy-ink sizzle)
  // harbour soundscape: continuous sea wash + occasional gull cries out over the water
  _updateAmbience(dt) {
    if (!this._audioOn || !G.audio?.loop) return;
    if (!this._amb) this._amb = G.audio.loop('harbor_ambience', { volume: 0.55 });
    // in the lobby's alley the harbour is only a distant wash (and no gulls overhead)
    const inSet = !!this.showcase?.fullFrame;
    this._ambV = damp(this._ambV ?? 0.55, inSet ? 0.12 : 0.55, 2, dt);
    this._amb.set?.({ volume: this._ambV });
    if (inSet) return;
    this._gullT = (this._gullT ?? 4) - dt;
    if (this._gullT <= 0) {
      this._gullT = 7 + Math.random() * 12;
      const B = G.level.bounds, a = Math.random() * Math.PI * 2;
      const p = this._gullP || (this._gullP = new THREE.Vector3());
      p.set(Math.cos(a) * (B.maxX + 25), 12 + Math.random() * 8, Math.sin(a) * (B.maxZ + 20));
      G.audio.play('gull', { pos: p, volume: 0.6 + Math.random() * 0.4, pitch: 0.9 + Math.random() * 0.25 });
    }
    // marina: rigging ringing against the masts in the gusts, and now and then a ship's horn out in the channel
    if (G.level?.layout?.id === 'halyard') {
      const p = this._ambP || (this._ambP = new THREE.Vector3());
      this._gustT = (this._gustT ?? 2) - dt;
      if (this._gustT <= 0) { this._gustT = 2.5 + Math.random() * 4; this._clinks = 2 + ((Math.random() * 4) | 0); this._clinkT = 0; }
      if (this._clinks > 0 && (this._clinkT -= dt) <= 0) {
        this._clinks--; this._clinkT = 0.12 + Math.random() * 0.45;
        const s = Math.random() < 0.5 ? -1 : 1;
        p.set(s * (27 + Math.random() * 14), 8 + Math.random() * 4, (Math.random() * 2 - 1) * 40);
        G.audio.play('halyard_clink', { pos: p, volume: 0.5 + Math.random() * 0.5, pitch: 0.85 + Math.random() * 0.35 });
      }
      this._hornT = (this._hornT ?? 28 + Math.random() * 10) - dt;
      if (this._hornT <= 0) {
        this._hornT = 55 + Math.random() * 30;
        p.set((Math.random() < 0.5 ? -1 : 1) * 95, 8, (Math.random() * 2 - 1) * 70);
        G.audio.play('ferry_horn', { pos: p, volume: 0.8 });
      }
    }
  }

  _updateLocalLoops(dt) {
    const m = this.match, a = m && !m.attract && !m.paused ? m.local : null;
    const L = this._loops || (this._loops = {});
    const want = (name, on, vol, pitch = 1) => {
      if (on && !L[name]) L[name] = G.audio?.loop?.(name, { volume: 0 });
      const h = L[name];
      if (!h) return;
      h._v = damp(h._v || 0, on ? vol : 0, on ? 10 : 7, dt);
      h.set({ volume: h._v, pitch });
      if (!on && h._v < 0.01) { h.stop(0.05); L[name] = null; }
    };
    const alive = !!(a && a.alive);
    const hs = alive ? Math.hypot(a.vel.x, a.vel.z) : 0;
    want('swim', alive && a.anim.form === 'swim' && hs > 0.5, Math.min(0.6, hs / 11.8 * 0.6 + 0.08), 0.6 + Math.min(1, hs / 11.8));
    want('climb', alive && a.anim.form === 'climb', 0.5, alive ? 0.6 + Math.min(1, Math.abs(a.vel.y) / 7.5) : 1);
    want('enemy_ink_sizzle', alive && a.grounded && a.groundTeam === 2, 0.45, 1.0);
  }

  _padMenus() {
    const inp = this.input;
    if (!inp.pad) return;
    const pp = inp.padPressed;
    if (this.menus?.current) {
      const nav = (d) => this.menus.nav?.(d);
      if (pp.has(12)) nav('up'); if (pp.has(13)) nav('down'); if (pp.has(14)) nav('left'); if (pp.has(15)) nav('right');
      if (pp.has(0)) nav('accept'); if (pp.has(1)) nav('back'); if (pp.has(2)) nav('alt');   // X: locker shuffle etc.
      if (pp.has(4)) nav('tab_prev'); if (pp.has(5)) nav('tab_next');
      if (pp.has(3)) nav('alt');
      // left stick as d-pad with repeat
      const ly = inp.padAxis(1), lx = inp.padAxis(0);
      this._stickT = (this._stickT || 0) - 1 / 60;
      if (this._stickT <= 0) {
        if (ly < -0.6) { nav('up'); this._stickT = 0.22; } else if (ly > 0.6) { nav('down'); this._stickT = 0.22; }
        else if (lx < -0.6) { nav('left'); this._stickT = 0.22; } else if (lx > 0.6) { nav('right'); this._stickT = 0.22; }
      }
      if (pp.has(9) && this.menus.current === 'pause') this.resume();
    } else if (G.mode === 'match' && pp.has(9)) this.pause();
    else if (G.mode === 'match' && pp.has(8) && this.match?.practice) this.openPracticeLoadout();
  }

  _updateHud(dt) {
    const m = this.match, cam = G.camera;
    const a = this.hud?.viewAs?.alive ? this.hud.viewAs : m.local;   // spectate player view: the HUD is theirs
    this.minimap.update(dt);
    const w = a.weapon;
    // crosshair spread = the weapon's live cone (first-shot accurate, blooms with sustained fire / in the air)
    const vHalf = (G.camera.fov * Math.PI) / 360;
    const coneDeg = a.weaponRunner.spread ?? (w.kind === 'shooter' ? 5.5 : w.kind === 'blaster' ? 1.2 : 0);
    const spread = w.kind === 'roller' ? 28 : w.kind === 'brush' ? 22 : Math.min(90, (Math.tan((coneDeg * Math.PI) / 180) / Math.tan(vHalf)) * (innerHeight / 2));
    const players = [];
    const t = { x: 0, y: 0 };
    for (const o of m.actors) {
      if (!o.alive) continue;
      const tracked = o.team !== a.team && o.status.track > 0 && o.status.trackTeam === a.team;
      if (o.team !== a.team && !o.isLocal) {
        // enemies only show on the map when visible to your team (not submerged far away) — tracked ones always do
        if (o.anim.form === 'swim' && !tracked) continue;
      }
      this.minimap.toCanvas(o.pos.x, o.pos.z, t);
      players.push({ x: t.x / this.minimap.w, y: t.y / this.minimap.h, team: o.team, isSelf: o === a, yaw: -o.yaw + (this.minimap.flip ? Math.PI : 0), alive: o.alive, color: G.teamHex[o.team], tracked });
    }
    // ally markers
    const markers = [];
    const v = this._mv || (this._mv = new THREE.Vector3());
    const W = innerWidth, H = innerHeight;
    for (const o of m.actors) {
      const tracked = o.team !== a.team && o.alive && o.status.track > 0 && o.status.trackTeam === a.team;
      if (!tracked && (o === a || o.team !== a.team || !o.alive)) continue;
      if (o.character.getHeadPosition && o.form !== 'squid') { o.character.getHeadPosition(v); v.y += 0.45; }
      else { if (o.visualPos) o.visualPos(v); else v.copy(o.pos); v.y += o.form === 'squid' ? 1.0 : 1.9; }
      v.project(cam);
      const behind = v.z > 1;
      let x = (v.x * 0.5 + 0.5) * W, y = (-v.y * 0.5 + 0.5) * H;
      const onScreen = !behind && x > 20 && x < W - 20 && y > 20 && y < H - 20;
      let angle = 0;
      if (!onScreen) {
        let dx = x - W / 2, dy = y - H / 2;
        if (behind) { dx = -dx; dy = -dy; }
        angle = Math.atan2(dy, dx);
        const k = Math.min((W / 2 - 40) / Math.max(1e-3, Math.abs(Math.cos(angle))), (H / 2 - 40) / Math.max(1e-3, Math.abs(Math.sin(angle))));
        x = W / 2 + Math.cos(angle) * k; y = H / 2 + Math.sin(angle) * k;
      }
      markers.push({ x, y, name: o.name, color: G.teamHex[o.team], onScreen, angle, dist: o.pos.distanceTo(a.pos), tracked });
    }
    // Zone Control: a marker over each live objective (pinned to the screen edge with an arrow when it's off screen)
    const zoneMk = [];
    if (m.zones && m.state === 'playing' && !m.zones.winner) {
      for (const z of m.zones.active.zones) {
        v.set(z.center[0], z.center[1] + 2.6, z.center[2]);
        const dist = Math.hypot(z.center[0] - a.pos.x, z.center[2] - a.pos.z);
        v.project(cam);
        const behind = v.z > 1;
        let x = (v.x * 0.5 + 0.5) * W, y = (-v.y * 0.5 + 0.5) * H;
        const onScreen = !behind && x > 60 && x < W - 60 && y > 70 && y < H - 60;
        let angle = 0;
        if (!onScreen) {
          let dx = x - W / 2, dy = y - H / 2;
          if (behind) { dx = -dx; dy = -dy; }
          angle = Math.atan2(dy, dx);
          const k = Math.min((W / 2 - 70) / Math.max(1e-3, Math.abs(Math.cos(angle))), (H / 2 - (dy > 0 ? 140 : 80)) / Math.max(1e-3, Math.abs(Math.sin(angle))));   // clear of the bottom hint bar
          x = W / 2 + Math.cos(angle) * k; y = H / 2 + Math.sin(angle) * k;
        }
        const owner = z.owner === 0 || z.owner === 1 ? z.owner : -1;
        zoneMk.push({ x, y, onScreen, angle, dist, color: owner >= 0 ? G.teamHex[owner] : null, ours: owner === a.team });
      }
    }
    // contextual prompts (light tutorial)
    this._hintT += dt;
    let prompt = null;
    const inkF = a.ink / PLAYER.inkMax;
    if (m.state === 'playing' && a.alive) {
      if (m.controller?.mapHeld) prompt = null;   // the map diorama carries its own super-jump hints
      else if (a.superJumpState) prompt = null;
      else if (this._lowInkFlash > 0) { this._lowInkFlash -= dt; prompt = { id: 'Low slime! Hold SHIFT in your slime to refill' }; }
      else if (a.specialReady() && (this._hints.specialT = (this._hints.specialT || 0) + dt) > 2) prompt = { id: 'Special ready! Press F' };
      else if (inkF < 0.25 && a.form !== 'squid') prompt = { id: 'Hold SHIFT to swim in your slime and refill' };
      else if (m.duration - m.time < 8 && !this._hints.shot) prompt = m.zones ? { id: 'Slime the zone and hold it to count down!' } : { kind: 'turfTip', id: 'Paint the ground — most turf wins!' };
      if (!a.specialReady()) this._hints.specialT = 0;
      if (a.intent.fire) this._hints.shot = true;
    }
    if (m.practice && m.state === 'playing' && a.alive && this._hintT < 7) prompt = { id: 'Practice · L to change loadout · ESC for the practice menu' };
    const strikeAim = !!(a.specialActive && a.specialActive.id === 'strike' && a.specialActive.aiming);
    // "Yeah!" cheers + mid-game Emote bubbles → screen positions over the player (anyone on screen)
    const cheers = [];
    for (const c of G.specials.cheers) {
      const o = c.a;
      v.set(o.pos.x, o.pos.y + (o.smoothY || 0) + 2.25, o.pos.z).project(cam);
      if (v.z > 1 || Math.abs(v.x) > 1.1 || Math.abs(v.y) > 1.1) continue;
      cheers.push({ x: (v.x * 0.5 + 0.5) * W, y: (-v.y * 0.5 + 0.5) * H, k: c.t / 1.1, color: G.teamHex[o.team], text: 'YEAH!' });
    }
    for (const o of m.actors) {
      if (!o.alive || !o.emote || !o.emoteSpec) continue;
      v.set(o.pos.x, o.pos.y + (o.smoothY || 0) + 2.35, o.pos.z).project(cam);
      if (v.z > 1 || Math.abs(v.x) > 1.1 || Math.abs(v.y) > 1.1) continue;
      const k = clamp(o.emoteTimer / Math.max(0.5, o.emoteDur || 3), 0, 1);
      cheers.push({ x: (v.x * 0.5 + 0.5) * W, y: (-v.y * 0.5 + 0.5) * H, k: k * 0.45, color: G.teamHex[o.team], text: `${o.emoteSpec.emoji} ${o.emoteSpec.label}!` });
    }
    // death markers: screen spot (floating a little over where they fell), size by camera distance, minimap spot —
    // written into the pooled records the HUD reads (no per-frame garbage)
    const deaths = this._deathPool();
    for (const d of deaths) {
      if (!d.on) continue;
      d.ally = d.team === a.team;
      v.set(d.x, d.y + 0.95, d.z);
      const dist = v.distanceTo(cam.position);
      v.project(cam);
      d.vis = v.z < 1 && Math.abs(v.x) < 1.08 && Math.abs(v.y) < 1.08;
      d.sx = (v.x * 0.5 + 0.5) * W; d.sy = (-v.y * 0.5 + 0.5) * H;
      d.sc = clamp(1.12 - dist / 60, 0.56, 1);
      this.minimap.toCanvas(d.x, d.z, t); d.mx = t.x / this.minimap.w; d.my = t.y / this.minimap.h;
    }
    if (a.specialActive && m.state === 'playing' && a.alive) prompt = G.specials.prompt(a) || prompt;
    else if (m.state === 'playing' && a.alive && m.actors.some((o) => o !== a && o.team === a.team && o.specialActive?.id === 'booyah' && !o.specialActive.thrown)) prompt = { id: 'A teammate is charging a Cheer Orb — press C to cheer it on!' };
    const frame = {
      time: m.practice ? null : m.time,
      teams: a.team === 1 ? m.teamSummary().reverse() : m.teamSummary(),   // HUD: [your team, theirs]
      ink: a.ink / PLAYER.inkMax, inkLow: a.ink < 18 || (this._lowInkFlash > 0), subCost: a.specialActive?.kind === 'barrage' ? 0 : (a.sub || SUB.bomb).inkCost / PLAYER.inkMax, subKind: (a.specialActive?.kind === 'barrage' ? a.specialActive.bomb : a.sub || SUB.bomb).kind,
      poisoned: a.status.poison > 0, tracked: a.status.track > 0,
      special: a.specialActive ? G.specials.remaining(a) : a.specialFrac(), specialReady: a.specialReady(), specialActive: !!a.specialActive, specialId: a.specialId,
      hp: a.hp / PLAYER.hp,
      weapon: a.weaponId, charge: a.weaponRunner.charge,
      twin: w.kind === 'twins' ? { planted: !!a.weaponRunner.turret, rolls: a.weaponRunner.rollsLeft() } : null,
      crosshair: { spread, onTarget: m.controller?.onTarget ? 'enemy' : null, inRange: m.controller ? m.controller.inRange !== false : true },
      // corner minimap follows the setting; the TAB map (needed for super jumps) is always available
      map: (this.settings.minimap !== false || strikeAim) ? { canvas: this.minimap.canvas, expanded: strikeAim, strike: strikeAim, players } : null,
      markers,
      zoneMk,
      cheers,
      deaths,
      // a Super Jump planned on the TAB map while splatted ({ kind, target, name } | null): shown on the splat screen
      jumpQueue: !a.alive ? m.controller?.jumpQueue || null : null,
      prompt,
      fps: this.settings.showFps ? this.fps : undefined,
      // Zone Control: counts / penalties / the operational objective (HUD counters, objective chip, banners)
      zones: m.zones ? { ...m.zones.state(), viewer: a.team } : undefined,
    };
    this.hud.update(dt, frame);
  }
}

const game = new Game();
game.boot().catch((e) => {
  console.error(e);
  const el = document.getElementById('boot-error');
  if (el) { el.textContent = t('Something went wrong while loading') + ': ' + e.message; el.style.display = 'block'; }
});
